/**
 * Presupuestos SA — Cotizador Diseño (Soluciones Arquitectónicas)
 * Routes: /presupuestos-sa (lista) · /presupuestos-sa/:id (editor)
 * Backend es la autoridad del cálculo: cada cambio refresca desde /api/sa/*
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api/axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Ruler, Plus, Trash, FloppyDisk, ArrowLeft, PencilSimple, CheckCircle, Eye, Copy,
  Gear, MagnifyingGlass, Hammer, CaretRight, User, X, FilePdf, BookmarkSimple, Camera as CameraIcon,
} from '@phosphor-icons/react';
import { CrearDesdeFoto } from '@/components/sa/CrearDesdeFoto';
import WizardPresupuesto from '@/components/sa/WizardPresupuesto';
import { MenuAyudaSA, AyudaTip } from '@/components/sa/MenuAyudaSA';
import { VariablesTecnicas } from '@/components/sa/VariablesTecnicas';
import { EjecucionCard } from '@/components/sa/EjecucionCard';
import { VariablesAdmin } from '@/components/sa/VariablesAdmin';
import { ReglasAdmin } from '@/components/sa/ReglasAdmin';
import { ConfigEjecucion } from '@/components/sa/ConfigEjecucion';
import { Disenador2D, PreviewDiseno, disenoDefault } from '@/components/sa/Disenador2D';
import { ComposerLego, composicionDefault } from '@/components/sa/ComposerLego';
import { EmpresaConfig } from '@/components/sa/EmpresaConfig';
import { CondicionesCard } from '@/components/sa/CondicionesCard';
import { PlantillasAdmin } from '@/components/sa/PlantillasAdmin';
import { CosteoInterno } from '@/components/sa/CosteoInterno';
import { PermisosSA } from '@/components/sa/PermisosSA';
import { ManualSA } from '@/components/sa/ManualSA';
import { RecetaConfig } from '@/components/sa/RecetaConfig';
import { Modulador, derivarDiseno } from '@/components/sa/Modulador';

const FAMILIAS_DISENO = ['ventanas', 'puertas', 'pabm', 'vtf'];
const dimsDiseno = (dz) => {
  if (!dz) return { ancho_mm: 0, alto_mm: 0 };
  if (dz.tipo === 'composicion') {
    const pz = dz.piezas || [];
    if (!pz.length) return { ancho_mm: 0, alto_mm: 0 };
    const x0 = Math.min(...pz.map((p) => Number(p.x) || 0)), y0 = Math.min(...pz.map((p) => Number(p.y) || 0));
    return {
      ancho_mm: pz.reduce((m, p) => Math.max(m, (Number(p.x) || 0) + (Number(p.ancho) || 0)), 0) - x0,
      alto_mm: pz.reduce((m, p) => Math.max(m, (Number(p.y) || 0) + (Number(p.alto) || 0)), 0) - y0,
    };
  }
  return { ancho_mm: Number(dz.ancho_mm) || 0, alto_mm: Number(dz.alto_mm) || 0 };
};

const ESTADOS = {
  borrador: { label: 'Borrador', color: 'bg-slate-500/20 text-slate-400' },
  en_calculo: { label: 'En cálculo', color: 'bg-cyan-500/20 text-cyan-400' },
  generado: { label: 'Generado', color: 'bg-indigo-500/20 text-indigo-400' },
  enviado: { label: 'Enviado', color: 'bg-blue-500/20 text-blue-400' },
  en_negociacion: { label: 'En negociación', color: 'bg-amber-500/20 text-amber-500' },
  aprobado: { label: 'Aprobado', color: 'bg-emerald-500/20 text-emerald-400' },
  rechazado: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400' },
  cancelado: { label: 'Cancelado', color: 'bg-zinc-500/20 text-zinc-400' },
};

const FAMILIAS = [
  { v: 'ventanas', label: 'Ventana' }, { v: 'puertas', label: 'Puerta' },
  { v: 'fachadas', label: 'Fachada' }, { v: 'vidrio', label: 'Vidrio' },
  { v: 'barandas', label: 'Baranda' }, { v: 'banos', label: 'Baño / división' },
  { v: 'techos_curvos', label: 'Techo curvo' }, { v: 'techos', label: 'Techo / cubierta' },
  { v: 'aluminio', label: 'Aluminio' }, { v: 'pvc', label: 'PVC' }, { v: 'personalizada', label: 'Otro' },
  { v: 'pabm', label: 'PABM — Puerta ducha abatible' }, { v: 'vtf', label: 'VTF — Vidrio fijo ducha' },
];
const famLabel = (v) => FAMILIAS.find((f) => f.v === v)?.label || v;

const Q = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const ADMIN_LIKE = ['admin', 'supervisor', 'gerente', 'financiero'];

// Miniatura del diseño técnico (solo geometría) para la lista de presupuestos
const MiniDiseno = ({ d }) => {
  if (!d?.cols_mm?.length) return null;
  const W = Math.max(1, d.cols_mm.reduce((a, b) => a + Number(b || 0), 0));
  const H = Math.max(1, Number((d.rows_mm || [1])[0]) || 1);
  let x = 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-14 h-10 shrink-0 border border-[var(--border)] rounded bg-white" preserveAspectRatio="xMidYMid meet" data-testid="pre-mini">
      {d.cols_mm.map((c, i) => {
        const cw = Number(c || 0); const m = (d.modulos || [])[i] || {};
        const ab = m.tipo_hoja === 'abatible';
        const sw = Math.min(W, H) * 0.012;
        const el = (
          <g key={i}>
            <rect x={x} y={0} width={cw} height={H} fill={ab ? '#E0F2FE' : '#F8FAFC'} stroke="#334155" strokeWidth={sw} />
            {ab && <path d={`M ${x + cw * 0.08} 0 L ${x + cw * 0.92} ${H / 2} L ${x + cw * 0.08} ${H}`} fill="none" stroke="#0284C7" strokeWidth={sw * 0.8} strokeDasharray={`${H * 0.05} ${H * 0.04}`} />}
          </g>
        );
        x += cw;
        return el;
      })}
    </svg>
  );
};


const EstadoBadge = ({ estado }) => {
  const e = ESTADOS[estado] || ESTADOS.borrador;
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${e.color}`}>{e.label}</span>;
};

const errMsg = (e, def) => e?.response?.data?.detail || def;

// =========================================================================
// LISTA
// =========================================================================
const Lista = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const adminLike = ADMIN_LIKE.includes((user?.role || '').toLowerCase());
  const [saPerms, setSaPerms] = useState(null);
  useEffect(() => { api.get('/sa/mis-permisos').then((r) => setSaPerms(r.data)).catch(() => {}); }, []);
  const cfgSA = saPerms ? saPerms.config : adminLike;   // configuración técnica/financiera
  const [showPermisos, setShowPermisos] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showMenuAyuda, setShowMenuAyuda] = useState(false);
  const [sinAcceso, setSinAcceso] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('all');
  const [vendorId, setVendorId] = useState('all');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [users, setUsers] = useState([]);
  const [showNuevo, setShowNuevo] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showFoto, setShowFoto] = useState(false);
  const [showMas, setShowMas] = useState(false);
  const [showFiltros, setShowFiltros] = useState(false);

  const [showConfig, setShowConfig] = useState(false);
  const [showCatalogo, setShowCatalogo] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [showReglas, setShowReglas] = useState(false);
  const [showCfgEj, setShowCfgEj] = useState(false);
  const [showEmpresa, setShowEmpresa] = useState(false);
  const [showPlantillas, setShowPlantillas] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (estado !== 'all') params.estado = estado;
      if (vendorId !== 'all') params.vendor_id = vendorId;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get('/sa/proyectos', { params });
      setItems(data.proyectos || []);
      setSinAcceso(false);
    } catch (e) {
      if (e?.response?.status === 403) setSinAcceso(true);
      else toast.error('Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  }, [q, estado, vendorId, desde, hasta]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (adminLike) api.get('/users').then((r) => setUsers(r.data || [])).catch(() => {});
  }, [adminLike]);

  const eliminar = (p) => {
    toast(`¿Eliminar el presupuesto ${p.numero}?`, {
      action: { label: 'Eliminar', onClick: async () => {
        try { await api.delete(`/sa/proyectos/${p.id}`); toast.success('Presupuesto eliminado'); load(); }
        catch (e) { toast.error(errMsg(e, 'No se pudo eliminar')); }
      } },
      cancel: { label: 'Cancelar' },
    });
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="presupuestos-list">
      {sinAcceso ? (
        <Card className="p-8 text-center space-y-2" data-testid="pre-sin-acceso">
          <Ruler size={36} weight="duotone" className="text-[var(--text-muted)] mx-auto" />
          <h1 className="text-xl font-black text-[var(--text)]">Sin acceso al módulo Presupuestos SA</h1>
          <p className="text-sm text-[var(--text-muted)]">Pide a administración que te asigne el permiso "Presupuestos SA" desde el botón Permisos del módulo.</p>
        </Card>
      ) : (
      <>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-[var(--text)] flex items-center gap-2">
            <Ruler size={28} weight="duotone" className="text-[#0EA5E9]" />
            Presupuestos SA
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">Cotizador técnico de Soluciones Arquitectónicas — proyectos, elementos y precios.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button onClick={() => setShowWizard(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white h-11 px-4 text-sm font-black" data-testid="pre-new-btn">
            <Plus size={18} weight="bold" className="mr-1" /> Nuevo presupuesto
          </Button>
          <Button variant="outline" onClick={() => setShowFoto(true)} className="h-11 px-4 text-sm font-black border-[#0EA5E9]/50 text-[#0EA5E9] hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" data-testid="pre-foto-btn">
            <CameraIcon size={18} weight="bold" className="mr-1" /> Crear desde foto/plano
          </Button>
          <Button variant="outline" onClick={() => navigate('/pizarra')} className="h-11 px-4 text-sm font-black border-[#6C3FB5]/50 text-[#6C3FB5] hover:bg-[#6C3FB5]/10 hover:text-[#6C3FB5]" data-testid="pre-pizarra-btn">
            🧠 Pizarra Inteligente
          </Button>
          <Button variant="outline" onClick={() => navigate('/clientes')} className="h-11 px-4 text-sm font-bold" data-testid="pre-clientes-btn">
            <User size={16} className="mr-1" /> Clientes
          </Button>
          <div className="relative">
            <Button variant="outline" size="sm" className="h-11 px-3" onClick={() => setShowMas((v) => !v)} data-testid="pre-mas-btn">
              <Gear size={16} className="mr-1" /> Más opciones
            </Button>
            {showMas && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMas(false)} />
                <div className="absolute right-0 top-12 z-50 bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl p-1.5 w-60 space-y-0.5" data-testid="pre-mas-menu">
                  {cfgSA && [
                    ['Catálogo Soluarq (con códigos cortos)', () => setShowCatalogo(true), 'pre-catalogo-btn'],
                    ['Valores generales', () => setShowVariables(true), 'pre-variables-btn'],
                    ['Reglas técnicas', () => setShowReglas(true), 'pre-reglas-btn'],
                    ['Costos de ejecución', () => setShowCfgEj(true), 'pre-cfgej-btn'],
                    ['Biblioteca de plantillas', () => setShowPlantillas(true), 'pre-plantillas-btn'],
                    ['Empresa / PDF', () => setShowEmpresa(true), 'pre-empresa-btn'],
                    ['Configuración', () => setShowConfig(true), 'pre-config-btn'],
                  ].map(([lbl, fn, tid]) => (
                    <button key={tid} type="button" onClick={() => { setShowMas(false); fn(); }} data-testid={tid}
                      className="w-full text-left text-xs font-bold text-[var(--text)] px-2.5 py-2 rounded-md hover:bg-[var(--surface-muted)]">{lbl}</button>
                  ))}
                  {adminLike && (
                    <button type="button" onClick={() => { setShowMas(false); setShowPermisos(true); }} data-testid="pre-permisos-btn"
                      className="w-full text-left text-xs font-bold text-[var(--text)] px-2.5 py-2 rounded-md hover:bg-[var(--surface-muted)]">Permisos</button>
                  )}
                  <button type="button" onClick={() => { setShowMas(false); setShowManual(true); }} data-testid="pre-manual-btn"
                    className="w-full text-left text-xs font-bold text-[var(--text)] px-2.5 py-2 rounded-md hover:bg-[var(--surface-muted)]">Ayuda / Manual</button>
                  <button type="button" onClick={() => { setShowMas(false); setShowMenuAyuda(true); }} data-testid="pre-menu-ayuda-btn"
                    className="w-full text-left text-xs font-bold text-[var(--text)] px-2.5 py-2 rounded-md hover:bg-[var(--surface-muted)]">¿Qué hay en cada sección?</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filtros — móvil: buscar siempre visible + panel de filtros colapsable */}
      <Card className="p-3 flex flex-col md:flex-row gap-2 md:items-center flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-2.5 text-[var(--text-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Número, cliente o proyecto…" className="pl-8 h-9" data-testid="pre-filter-q" />
        </div>
        <button type="button" className="md:hidden h-10 text-xs font-bold border border-[var(--border)] rounded-md text-[var(--text)]"
          onClick={() => setShowFiltros(!showFiltros)} data-testid="pre-filtros-toggle">
          {showFiltros ? 'Ocultar filtros ▲' : 'Filtros ▼'}
        </button>
        <div className={`${showFiltros ? 'flex' : 'hidden'} md:flex flex-col md:flex-row gap-2 md:items-center flex-wrap w-full md:w-auto`}>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="w-full md:w-[160px] h-9" data-testid="pre-filter-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {adminLike && (
          <Select value={vendorId} onValueChange={setVendorId}>
            <SelectTrigger className="w-full md:w-[170px] h-9" data-testid="pre-filter-vendedor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 items-center">
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-[140px]" data-testid="pre-filter-desde" />
          <span className="text-xs text-[var(--text-muted)]">a</span>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-[140px]" data-testid="pre-filter-hasta" />
        </div>
        </div>
      </Card>

      {/* Tabla desktop */}
      <Card className="overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left text-[11px] uppercase tracking-wide text-[var(--text-muted)]">
                <th className="px-3 py-2.5">Número</th>
                <th className="px-3 py-2.5">Cliente</th>
                <th className="px-3 py-2.5">Proyecto</th>
                <th className="px-3 py-2.5">Vendedor</th>
                <th className="px-3 py-2.5">Fecha</th>
                <th className="px-3 py-2.5">Estado</th>
                <th className="px-3 py-2.5 text-right">Total</th>
                <th className="px-3 py-2.5">Últ. modificación</th>
                <th className="px-3 py-2.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--text-muted)]">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--text-muted)]">Sin presupuestos. Crea el primero con “+ Nuevo Presupuesto”.</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-b border-[var(--border)] hover:bg-[var(--bg)] cursor-pointer"
                    onClick={() => navigate(`/presupuestos-sa/${p.id}`)} data-testid={`pre-row-${p.numero}`}>
                  <td className="px-3 py-2.5 font-bold text-[#0EA5E9]"><span className="flex items-center gap-2">{p.mini && <MiniDiseno d={p.mini} />}{p.numero}</span></td>
                  <td className="px-3 py-2.5">{p.cliente_nombre || '—'}</td>
                  <td className="px-3 py-2.5">{p.nombre}</td>
                  <td className="px-3 py-2.5 text-[var(--text-muted)]">{p.vendor_name || '—'}</td>
                  <td className="px-3 py-2.5 text-[var(--text-muted)]">{fecha(p.created_at)}</td>
                  <td className="px-3 py-2.5"><EstadoBadge estado={p.estado} /></td>
                  <td className="px-3 py-2.5 text-right font-bold">{Q(p.totales?.total)}</td>
                  <td className="px-3 py-2.5 text-[var(--text-muted)]">{fecha(p.updated_at)}</td>
                  <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate(`/presupuestos-sa/${p.id}`)} data-testid={`pre-open-${p.numero}`}>
                        <PencilSimple size={15} />
                      </Button>
                      {adminLike && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" onClick={() => eliminar(p)} data-testid={`pre-del-${p.numero}`}>
                          <Trash size={15} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Cards móvil */}
      <div className="md:hidden space-y-2">
        {loading ? <Card className="p-6 text-center text-sm text-[var(--text-muted)]">Cargando…</Card>
          : items.length === 0 ? <Card className="p-6 text-center text-sm text-[var(--text-muted)]">Sin presupuestos.</Card>
          : items.map((p) => (
            <Card key={p.id} className="p-3" onClick={() => navigate(`/presupuestos-sa/${p.id}`)} data-testid={`pre-card-${p.numero}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex gap-2 min-w-0">
                  {p.mini && <MiniDiseno d={p.mini} />}
                  <div className="min-w-0">
                    <p className="font-bold text-[#0EA5E9] text-sm">{p.numero}</p>
                    <p className="text-sm font-semibold text-[var(--text)] truncate">{p.nombre}</p>
                    <p className="text-xs text-[var(--text-muted)] truncate">{p.cliente_nombre || 'Sin cliente'} · {p.vendor_name || ''}</p>
                    <p className="text-[10px] text-[var(--text-faint)]">{fecha(p.updated_at)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <EstadoBadge estado={p.estado} />
                  <p className="text-sm font-bold mt-1">{Q(p.totales?.total)}</p>
                </div>
              </div>
            </Card>
          ))}
      </div>

      {showWizard && <WizardPresupuesto onClose={() => { setShowWizard(false); load(); }} />}
      {showNuevo && <NuevoDialog adminLike={adminLike} users={users} onClose={() => setShowNuevo(false)}
        onCreated={(p) => { setShowNuevo(false); navigate(`/presupuestos-sa/${p.id}`); }} />}
      {showFoto && <CrearDesdeFoto open onClose={() => setShowFoto(false)}
        onCreated={(p) => { setShowFoto(false); navigate(`/presupuestos-sa/${p.id}`); }} />}
      {showConfig && <ConfigDialog onClose={() => setShowConfig(false)} />}
      {showCatalogo && <CatalogoDialog onClose={() => setShowCatalogo(false)} />}
      {showVariables && <VariablesAdmin familias={FAMILIAS} onClose={() => setShowVariables(false)} />}
      {showReglas && <ReglasAdmin familias={FAMILIAS} onClose={() => setShowReglas(false)} />}
      {showCfgEj && <ConfigEjecucion familias={FAMILIAS} onClose={() => setShowCfgEj(false)} />}
      {showEmpresa && <EmpresaConfig onClose={() => setShowEmpresa(false)} />}
      {showPlantillas && <PlantillasAdmin onClose={() => setShowPlantillas(false)} />}
      {showPermisos && <PermisosSA onClose={() => setShowPermisos(false)} />}
      {showManual && <ManualSA onClose={() => setShowManual(false)} />}
      {showMenuAyuda && <MenuAyudaSA open onClose={() => setShowMenuAyuda(false)} />}
      </>
      )}
    </div>
  );
};

// =========================================================================
// NUEVO PRESUPUESTO
// =========================================================================
const NuevoDialog = ({ adminLike, users, onClose, onCreated }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ nombre: '', cliente_nombre: '', cliente_telefono: '', cliente_id: '', direccion: '', vendor_id: '', notas: '' });
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (busca.length < 2) { setResultados([]); return; }
    const t = setTimeout(() => {
      api.get('/clientes', { params: { q: busca, limit: 15 } })   // módulo central CLIENTES
        .then((r) => setResultados((r.data.clientes || []).slice(0, 8)))
        .catch(() => setResultados([]));
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const crear = async () => {
    if (!form.nombre.trim()) return toast.error('Escribe el nombre del proyecto');
    setSaving(true);
    try {
      const { data } = await api.post('/sa/proyectos', { ...form, vendor_id: form.vendor_id || undefined });
      toast.success(`Presupuesto ${data.numero} creado`);
      onCreated(data);
    } catch (e) { toast.error(errMsg(e, 'No se pudo crear')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nuevo Presupuesto SA</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)]">Cliente (buscar en módulo Clientes)</label>
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nombre, teléfono, WhatsApp, correo o ID (CL-xxxx)…" data-testid="pre-nuevo-buscar-cliente" />
            {resultados.length > 0 && (
              <div className="border border-[var(--border)] rounded-lg mt-1 max-h-40 overflow-y-auto">
                {resultados.map((c, i) => (
                  <button key={i} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg)] flex justify-between gap-2"
                    data-testid={`pre-cliente-result-${i}`}
                    onClick={() => {
                      setForm({ ...form, cliente_id: c.id, cliente_nombre: c.nombre || '', cliente_telefono: c.telefono || c.whatsapp || '',
                        direccion: form.direccion || c.direccion || '' });
                      setBusca(''); setResultados([]);
                    }}>
                    <span className="truncate">{c.nombre} <span className="text-[10px] text-[#1D4ED8] font-mono">{c.id}</span></span>
                    <span className="text-[var(--text-muted)] text-xs shrink-0">{c.telefono || c.whatsapp || c.correo}</span>
                  </button>
                ))}
              </div>
            )}
            {form.cliente_id && <p className="text-[10px] text-emerald-600 font-bold mt-1" data-testid="pre-cliente-vinculado">✓ Vinculado a {form.cliente_id} — los datos se tomaron de la ficha maestra</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Nombre del cliente</label>
              <Input value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} data-testid="pre-nuevo-cliente" />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Teléfono</label>
              <Input value={form.cliente_telefono} onChange={(e) => setForm({ ...form, cliente_telefono: e.target.value })} data-testid="pre-nuevo-telefono" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)]">Proyecto *</label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Residencia Zona 16 — ventanería" data-testid="pre-nuevo-nombre" />
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)]">Dirección del proyecto</label>
            <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} data-testid="pre-nuevo-direccion" />
          </div>
          {adminLike && (
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Vendedor asignado</label>
              <Select value={form.vendor_id || user?.id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="pre-nuevo-vendedor"><SelectValue placeholder="Vendedor" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)]">Notas</label>
            <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="pre-nuevo-notas" />
          </div>
          <Button disabled={saving} onClick={crear} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="pre-nuevo-crear">
            {saving ? 'Creando…' : 'Crear y agregar elementos'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// CONFIG (admin)
// =========================================================================
const ConfigDialog = ({ onClose }) => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get('/sa/config').then((r) => setCfg(r.data)).catch(() => toast.error('Error al cargar config')); }, []);
  const guardar = async () => {
    try {
      await api.put('/sa/config', {
        metodo_utilidad: cfg.metodo_utilidad,
        utilidad_pct: Number(cfg.utilidad_pct),
        iva_pct: Number(cfg.iva_pct),
        descuento_max_vendedor_pct: Number(cfg.descuento_max_vendedor_pct),
      });
      toast.success('Configuración guardada (aplica a presupuestos nuevos)');
      onClose();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader><DialogTitle>Configuración del Cotizador SA</DialogTitle></DialogHeader>
        {!cfg ? <p className="text-sm text-[var(--text-muted)]">Cargando…</p> : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Método de utilidad</label>
              <Select value={cfg.metodo_utilidad} onValueChange={(v) => setCfg({ ...cfg, metodo_utilidad: v })}>
                <SelectTrigger data-testid="cfg-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="markup">Markup sobre costo — Precio = Costo × (1 + %)</SelectItem>
                  <SelectItem value="margen">Margen sobre precio — Precio = Costo / (1 − %)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)]">Utilidad %</label>
                <Input type="number" value={cfg.utilidad_pct} onChange={(e) => setCfg({ ...cfg, utilidad_pct: e.target.value })} data-testid="cfg-utilidad" />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)]">IVA %</label>
                <Input type="number" value={cfg.iva_pct} onChange={(e) => setCfg({ ...cfg, iva_pct: e.target.value })} data-testid="cfg-iva" />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)]">Desc. máx vendedor %</label>
                <Input type="number" value={cfg.descuento_max_vendedor_pct} onChange={(e) => setCfg({ ...cfg, descuento_max_vendedor_pct: e.target.value })} data-testid="cfg-desc-max" />
              </div>
            </div>
            <p className="text-[11px] text-[var(--text-muted)]">El método y % de utilidad aplican a presupuestos NUEVOS. Los existentes mantienen su método.</p>
            <Button onClick={guardar} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="cfg-guardar">Guardar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// CATÁLOGO TÉCNICO (admin)
// =========================================================================
const CatalogoDialog = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [nuevo, setNuevo] = useState({ codigo: '', nombre: '', tipo: 'material', unidad: 'unidad', costo: '', acabado: '', familia: '', codigo_corto: '', seccion: 'herraje', composicion: [] });
  const [hq, setHq] = useState('');
  const [hres, setHres] = useState(null);
  const [cortos, setCortos] = useState({});
  const [editId, setEditId] = useState(null);
  const [ops, setOps] = useState({ tipos: [], familias: [], acabados: [], unidades: [] });
  const fotoRef = React.useRef(null);
  const [fotoId, setFotoId] = useState(null);
  const subirFoto = async (f) => {
    if (!f || !fotoId) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      await api.post(`/sa/catalogo/${fotoId}/imagen`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto guardada — aparecerá en la planilla de herrajes del PDF');
      load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo subir la foto')); }
    setFotoId(null);
  };
  const [nuevaOp, setNuevaOp] = useState(null); // 'tipo' | 'familia' | 'acabado' | 'unidad'
  const [nuevaOpLabel, setNuevaOpLabel] = useState('');
  const ETIQ_FAM = { pabm: 'Puerta de baño abatible (PABM)', vtf: 'Vidrio templado fijo (VTF)', ventanas: 'Ventanas', puertas: 'Puertas', barandas: 'Barandas', fachadas: 'Fachadas' };
  ops.familias.forEach((o) => { ETIQ_FAM[o.valor] = o.label; });
  const tipoLabel = (v) => (v === 'mano_obra' ? 'M.O.' : (ops.tipos.find((o) => o.valor === v)?.label || v));
  const loadOps = () => api.get('/sa/catalogo/opciones').then((r) => setOps({ tipos: r.data.tipos || [], familias: r.data.familias || [], acabados: r.data.acabados || [], unidades: r.data.unidades || [] })).catch(() => {});
  const guardarOpcion = async () => {
    const label = nuevaOpLabel.trim();
    if (!label) return toast.error('Escribe el nombre');
    try {
      const { data } = await api.post('/sa/catalogo/opciones', { campo: nuevaOp, label });
      toast.success(`${{ tipo: 'Tipo', familia: 'Familia', acabado: 'Acabado', unidad: 'Unidad' }[nuevaOp]} "${data.label}" agregado — quedará guardado para futuros ingresos`);
      await loadOps();
      setNuevo((n) => ({ ...n, [nuevaOp]: ['acabado', 'unidad'].includes(nuevaOp) ? data.label.toLowerCase() : data.valor }));
      setNuevaOp(null); setNuevaOpLabel('');
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar la opción')); }
  };
  const renombrarOpcion = async (o, label) => {
    const v = (label || '').trim();
    if (!v || v === o.label) return;
    try { await api.patch(`/sa/catalogo/opciones/${o.id}`, { label: v }); toast.success(`Renombrado a "${v}"`); loadOps(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo renombrar')); }
  };
  const desactivarOpcion = async (o) => {
    try {
      await api.patch(`/sa/catalogo/opciones/${o.id}`, { activo: false });
      toast.success(`${{ tipo: 'Tipo', familia: 'Familia', acabado: 'Acabado', unidad: 'Unidad' }[o.campo]} "${o.label}" desactivado`);
      const cur = ['acabado', 'unidad'].includes(o.campo) ? o.label.toLowerCase() : o.valor;
      setNuevo((n) => (n[o.campo] === cur ? { ...n, [o.campo]: o.campo === 'tipo' ? 'material' : o.campo === 'unidad' ? 'unidad' : '' } : n));
      loadOps();
    } catch (e) { toast.error(errMsg(e, 'No se pudo desactivar')); }
  };
  const limpiarForm = () => { setEditId(null); setNuevo({ codigo: '', nombre: '', tipo: 'material', unidad: 'unidad', costo: '', acabado: '', familia: '', codigo_corto: '', seccion: 'herraje', composicion: [] }); };
  const exportarPDF = async () => {
    try {
      const r = await api.get('/sa/catalogo/pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'catalogo_soluarq.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo generar el PDF'); }
  };
  const loadCortos = () => api.get('/sa/codigos').then((r) => {
    const m = {};
    (r.data.codigos || []).forEach((c) => { if (c.activo !== false) m[c.item_codigo] = c; });
    setCortos(m);
  }).catch(() => {});
  const load = () => { api.get('/sa/catalogo').then((r) => setItems(r.data.items || [])).catch(() => {}); loadCortos(); loadOps(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const agregar = async () => {
    if (!nuevo.codigo || !nuevo.nombre) return toast.error('Código y nombre son requeridos');
    try {
      if (editId) await api.put(`/sa/catalogo/${editId}`, { ...nuevo, costo: Number(nuevo.costo || 0), composicion: (nuevo.composicion || []).filter((c) => c.item_codigo) });
      else await api.post('/sa/catalogo', { ...nuevo, costo: Number(nuevo.costo || 0), composicion: (nuevo.composicion || []).filter((c) => c.item_codigo) });
      if (nuevo.codigo_corto.trim()) {
        try { await api.post('/sa/codigos', { codigo: nuevo.codigo_corto, item_codigo: nuevo.codigo, observaciones: 'Asignado desde Catálogo Soluarq' }); }
        catch (e) { toast.error(errMsg(e, 'Item creado, pero el código corto no se pudo guardar')); }
      }
      setEditId(null);
      setNuevo({ codigo: '', nombre: '', tipo: 'material', unidad: 'unidad', costo: '', acabado: '', familia: '', codigo_corto: '', seccion: 'herraje', composicion: [] });
      toast.success(editId ? 'Producto actualizado' : 'Item agregado');
      load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };
  const setCorto = async (it, valor) => {
    const v = (valor || '').trim().toUpperCase();
    const actual = cortos[it.codigo];
    if (!v && actual) {
      try { await api.patch(`/sa/codigos/${actual.id}`, { activo: false }); toast.success(`Código corto ${actual.codigo} desactivado`); loadCortos(); }
      catch (e) { toast.error(errMsg(e, 'No se pudo desactivar')); }
      return;
    }
    if (!v || v === actual?.codigo) return;
    try { await api.post('/sa/codigos', { codigo: v, item_codigo: it.codigo, observaciones: 'Asignado desde Catálogo Soluarq' }); toast.success(`${v} → ${it.nombre}`); loadCortos(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo guardar el código corto')); }
  };
  const borrar = async (it) => {
    try { await api.delete(`/sa/catalogo/${it.id}`); load(); } catch (e) { toast.error(errMsg(e, 'Error')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Catálogo Soluarq</DialogTitle></DialogHeader>
        <input ref={fotoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => { subirFoto(e.target.files?.[0]); e.target.value = ''; }} data-testid="cat-foto-input" />
        <div className="flex gap-1.5 items-center">
          <Input placeholder="Buscar en HarpERP (SKU / nombre)…" value={hq} onChange={(e) => setHq(e.target.value)} className="h-8 text-xs flex-1" data-testid="cat-harperp-q" />
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="cat-harperp-buscar" onClick={async () => {
            try { const r = await api.get('/sa/soluarq/harperp', { params: { q: hq } }); setHres(r.data.productos || []); }
            catch (e) { toast.error(errMsg(e, 'HarpERP no disponible')); }
          }}>+ Desde HarpERP</Button>
        </div>
        {hres && (
          <div className="border border-dashed border-[var(--border)] rounded-lg p-1.5 max-h-36 overflow-y-auto space-y-1">
            {hres.length === 0 && <p className="text-[11px] text-[var(--text-muted)]">Sin resultados.</p>}
            {hres.map((p) => (
              <div key={p.sku} className="flex items-center justify-between text-[11px] gap-2">
                <span className="truncate"><b className="text-[#0EA5E9]">{p.sku}</b> {p.nombre}</span>
                <Button size="sm" variant="ghost" className="h-6 text-[11px] shrink-0" data-testid={`cat-importar-${p.sku}`} onClick={async () => {
                  try { await api.post('/sa/soluarq/importar-harperp', { sku: p.sku, nombre: p.nombre, costo: p.costo || 0 }); toast.success(`SLQ-${p.sku} importado`); load(); }
                  catch (e) { toast.error(errMsg(e, 'No se pudo importar')); }
                }}>Copiar</Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-6 gap-1.5 items-end">
          <Input placeholder="Código" value={nuevo.codigo} onChange={(e) => setNuevo({ ...nuevo, codigo: e.target.value.toUpperCase() })} className="col-span-1 h-9 text-xs" data-testid="cat-codigo" />
          <Input placeholder="Nombre" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} className="col-span-2 h-9 text-xs" data-testid="cat-nombre" />
          <Select value={nuevo.tipo} onValueChange={(v) => { if (v === '__add__') { setNuevaOp('tipo'); setNuevaOpLabel(''); } else setNuevo({ ...nuevo, tipo: v }); }}>
            <SelectTrigger className="h-9 text-xs" data-testid="cat-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="material">Material</SelectItem>
              <SelectItem value="mano_obra">Mano de obra</SelectItem>
              <SelectItem value="otro">Otro</SelectItem>
              {ops.tipos.map((o) => <SelectItem key={o.valor} value={o.valor}>{o.label}</SelectItem>)}
              <SelectItem value="__add__" data-testid="cat-tipo-add">+ Agregar tipo…</SelectItem>
            </SelectContent>
          </Select>
          <select value={nuevo.unidad} onChange={(e) => { const v = e.target.value; if (v === '__add__') { setNuevaOp('unidad'); setNuevaOpLabel(''); } else setNuevo({ ...nuevo, unidad: v }); }}
            className="h-9 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="cat-unidad">
            {(() => {
              const std = ['unidad', 'metro', 'm²', 'metro lineal', 'pieza', 'juego', 'par', 'lámina', 'galón', 'hora'];
              const custom = ops.unidades.map((o) => o.label.toLowerCase());
              const todas = [...std, ...custom.filter((u) => !std.includes(u))];
              if (nuevo.unidad && !todas.includes(nuevo.unidad)) todas.unshift(nuevo.unidad);
              return todas.map((u) => <option key={u} value={u}>{u}</option>);
            })()}
            <option value="__add__">+ Agregar unidad…</option>
          </select>
          <Input placeholder="Costo Q" type="number" value={nuevo.costo} onChange={(e) => setNuevo({ ...nuevo, costo: e.target.value })} className="h-9 text-xs" data-testid="cat-costo" />
        </div>
        <div className="flex gap-1.5">
          <select value={nuevo.acabado} onChange={(e) => { const v = e.target.value; if (v === '__add__') { setNuevaOp('acabado'); setNuevaOpLabel(''); } else setNuevo({ ...nuevo, acabado: v }); }}
            className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="cat-acabado">
            <option value="">Acabado —</option>
            {['satinado', 'cromado', 'negro', 'bronce', 'oro', 'otro'].map((a) => <option key={a} value={a}>{a}</option>)}
            {ops.acabados.map((o) => <option key={o.valor} value={o.label.toLowerCase()}>{o.label}</option>)}
            <option value="__add__">+ Agregar acabado…</option>
          </select>
          <select value={nuevo.familia} onChange={(e) => { const v = e.target.value; if (v === '__add__') { setNuevaOp('familia'); setNuevaOpLabel(''); } else setNuevo({ ...nuevo, familia: v }); }}
            className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="cat-familia">
            <option value="">Familia —</option>
            {FAMILIAS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            {ops.familias.map((o) => <option key={o.valor} value={o.valor}>{o.label}</option>)}
            <option value="__add__">+ Agregar familia…</option>
          </select>
          <Input placeholder="Código corto (ej. 2S)" value={nuevo.codigo_corto} title="Alias para escribir en planos/dibujos — apunta a este producto, no sustituye el SKU"
            onChange={(e) => setNuevo({ ...nuevo, codigo_corto: e.target.value.toUpperCase() })} className="h-8 text-xs w-[130px] uppercase" data-testid="cat-codigo-corto" />
          <select value={nuevo.seccion} onChange={(e) => setNuevo({ ...nuevo, seccion: e.target.value })}
            title="Elemento = pieza insertable en el Compositor Lego (ventana, puerta, vidrio, pasamanos…) con su composición interna"
            className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid="cat-seccion">
            <option value="herraje">Herraje / material</option>
            <option value="elemento">Elemento (Lego)</option>
          </select>
        </div>
        {nuevo.seccion === 'elemento' && (
          <div className="border border-dashed border-emerald-500/60 rounded-lg p-2 space-y-1" data-testid="cat-composicion">
            <p className="text-[10px] font-bold text-[var(--text-muted)]">🧱 Composición del elemento — qué lleva por dentro (perfiles, vidrio, rodos, felpa, chapas…). Todo apunta a este mismo catálogo.</p>
            {(nuevo.composicion || []).map((c, i) => (
              <div key={i} className="flex gap-1.5 items-center" data-testid={`cat-comp-fila-${i}`}>
                <select value={c.item_codigo} onChange={(e) => setNuevo({ ...nuevo, composicion: nuevo.composicion.map((x, j) => (j === i ? { ...x, item_codigo: e.target.value } : x)) })}
                  className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1 flex-1" data-testid={`cat-comp-item-${i}`}>
                  <option value="">— elegir del catálogo —</option>
                  {items.filter((x) => x.codigo !== nuevo.codigo).map((x) => <option key={x.codigo} value={x.codigo}>{x.codigo} · {x.nombre}</option>)}
                </select>
                <Input type="number" value={c.cantidad} onChange={(e) => setNuevo({ ...nuevo, composicion: nuevo.composicion.map((x, j) => (j === i ? { ...x, cantidad: Number(e.target.value || 1) } : x)) })}
                  className="h-8 w-16 text-xs" title="Cantidad" data-testid={`cat-comp-cant-${i}`} />
                <Button size="sm" variant="ghost" className="h-8 px-1.5 text-red-400" onClick={() => setNuevo({ ...nuevo, composicion: nuevo.composicion.filter((_, j) => j !== i) })} data-testid={`cat-comp-del-${i}`}><Trash size={13} /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setNuevo({ ...nuevo, composicion: [...(nuevo.composicion || []), { item_codigo: '', cantidad: 1, nota: '' }] })} data-testid="cat-comp-add"><Plus size={12} className="mr-1" />Agregar componente</Button>
          </div>
        )}
        {nuevaOp && (
          <div className="flex gap-1.5 items-center border border-dashed border-[#0EA5E9] rounded-lg p-2" data-testid="cat-nueva-opcion">
            <Input autoFocus placeholder={{ tipo: 'Nuevo tipo (ej. Flete)', familia: 'Nueva familia (ej. Duchas)', acabado: 'Nuevo acabado (ej. Oro rosa)', unidad: 'Nueva unidad (ej. rollo)' }[nuevaOp]} value={nuevaOpLabel}
              onChange={(e) => setNuevaOpLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && guardarOpcion()}
              className="h-8 text-xs flex-1" data-testid="cat-nueva-opcion-input" />
            <Button size="sm" className="h-8 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white" onClick={guardarOpcion} data-testid="cat-nueva-opcion-guardar">Guardar {nuevaOp}</Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setNuevaOp(null); setNuevaOpLabel(''); }} data-testid="cat-nueva-opcion-cancelar">Cancelar</Button>
          </div>
        )}
        {(ops.tipos.length > 0 || ops.familias.length > 0 || ops.acabados.length > 0 || ops.unidades.length > 0) && (
          <details className="border border-[var(--border)] rounded-lg px-2 py-1.5" data-testid="cat-ops-gestionar">
            <summary className="text-[11px] font-bold text-[var(--text-muted)] cursor-pointer select-none">
              Gestionar opciones personalizadas ({ops.tipos.length + ops.familias.length + ops.acabados.length + ops.unidades.length}) — renombrar o desactivar
            </summary>
            <div className="mt-1.5 space-y-1">
              {[...ops.tipos, ...ops.familias, ...ops.acabados, ...ops.unidades].map((o) => (
                <div key={o.id} className="flex items-center gap-1.5 text-[11px]" data-testid={`cat-op-${o.valor}`}>
                  <span className={`shrink-0 px-1.5 py-0.5 rounded font-bold uppercase text-[9px] ${o.campo === 'tipo' ? 'bg-[#0EA5E9]/15 text-[#0EA5E9]' : o.campo === 'familia' ? 'bg-amber-500/15 text-amber-500' : o.campo === 'acabado' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-violet-500/15 text-violet-500'}`}>{o.campo}</span>
                  <input defaultValue={o.label} title="Edita el nombre y sal del campo para guardar"
                    className="h-7 flex-1 text-[11px] border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1.5"
                    onBlur={(e) => renombrarOpcion(o, e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    data-testid={`cat-op-rename-${o.valor}`} />
                  <Button size="sm" variant="ghost" className="h-7 px-1.5 text-red-400 text-[11px]" title="Desactivar (deja de aparecer en los selectores)"
                    onClick={() => desactivarOpcion(o)} data-testid={`cat-op-off-${o.valor}`}><Trash size={13} /></Button>
                </div>
              ))}
            </div>
          </details>
        )}
        <div className="flex gap-1.5">
          <Button size="sm" onClick={agregar} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="cat-agregar">
            {editId ? <><FloppyDisk size={14} className="mr-1" /> Guardar cambios</> : <><Plus size={14} className="mr-1" /> Agregar item</>}
          </Button>
          {editId && <Button size="sm" variant="outline" onClick={limpiarForm} data-testid="cat-cancelar-edit">Cancelar edición</Button>}
          <Button size="sm" variant="outline" onClick={exportarPDF} data-testid="cat-pdf"><FilePdf size={14} className="mr-1" /> Exportar PDF</Button>
        </div>
        {(() => {
          const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const q = norm(filtro.trim());
          const visibles = !q ? items : items.filter((it) => {
            const famTxt = ETIQ_FAM[it.familia] || it.familia || 'general';
            return [it.codigo, it.nombre, it.familia, famTxt, cortos[it.codigo]?.codigo].some((v) => norm(v).includes(q));
          });
          return (
            <>
              <div className="flex gap-1.5 items-center">
                <Input placeholder="Filtrar por nombre, código, cód. corto o familia…" value={filtro} onChange={(e) => setFiltro(e.target.value)}
                  className="h-8 text-xs flex-1" data-testid="cat-filtro" />
                {filtro && (
                  <>
                    <span className="text-[11px] text-[var(--text-muted)] shrink-0" data-testid="cat-filtro-count">{visibles.length} de {items.length}</span>
                    <Button size="sm" variant="ghost" className="h-8 text-xs px-2" onClick={() => setFiltro('')} data-testid="cat-filtro-limpiar">✕</Button>
                  </>
                )}
              </div>
              <div className="border border-[var(--border)] rounded-lg max-h-[45vh] overflow-y-auto">
                {items.length === 0 ? <p className="p-4 text-sm text-[var(--text-muted)] text-center">Catálogo vacío — agrega perfiles, vidrios, herrajes y mano de obra con su costo.</p>
                  : visibles.length === 0 ? <p className="p-4 text-sm text-[var(--text-muted)] text-center" data-testid="cat-filtro-vacio">Sin resultados para "{filtro}".</p>
                  : Object.entries(visibles.reduce((g, it) => { const k = ETIQ_FAM[it.familia] || (it.familia ? it.familia : 'General'); (g[k] = g[k] || []).push(it); return g; }, {}))
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([fam, arr]) => (
                <div key={fam}>
                  <p className="px-3 py-1.5 text-[10px] font-black uppercase bg-[var(--bg)] text-[#0EA5E9] border-y border-[var(--border)] sticky top-0" data-testid={`cat-grupo-${fam}`}>{fam} · {arr.length}</p>
                  <div className="divide-y divide-[var(--border)]">
                  {arr.map((it) => (
              <div key={it.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`cat-item-${it.codigo}`}>
                <div>
                  {it.imagen_path && (
                    <img src={`${api.defaults.baseURL}/sa/catalogo/imagen/${it.imagen_path}`} alt={it.codigo}
                      className="inline-block w-8 h-8 object-cover rounded border border-[var(--border)] mr-2 align-middle" data-testid={`cat-foto-${it.codigo}`} />
                  )}
                  <span className="font-bold text-[#0EA5E9] mr-2">{it.codigo}</span>
                  {it.nombre}
                  {it.seccion === 'elemento' && <span className="ml-1.5 text-[8px] font-black px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-600 uppercase" data-testid={`cat-badge-elemento-${it.codigo}`}>Elemento{(it.composicion || []).length ? ` · ${it.composicion.length} comp.` : ''}</span>}
                  <span className="text-xs text-[var(--text-muted)] ml-2">({it.unidad} · {tipoLabel(it.tipo)}{it.acabado ? ` · ${it.acabado}` : ''})</span>
                </div>
                <div className="flex items-center gap-2">
                  <input defaultValue={cortos[it.codigo]?.codigo || ''} placeholder="cód. corto" title="Código corto para planos (vacío = desactivar)"
                    className="h-7 w-[76px] text-[11px] uppercase text-center border border-[var(--border)] rounded-md bg-[var(--card)] text-amber-500 font-bold"
                    onBlur={(e) => setCorto(it, e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    data-testid={`cat-corto-${it.codigo}`} />
                  <span className="font-bold">{Q(it.costo)}</span>
                  <Button variant="ghost" size="sm" className="h-7 px-1.5" title="Editar producto"
                    onClick={() => { setEditId(it.id); setNuevo({ codigo: it.codigo || '', nombre: it.nombre || '', tipo: it.tipo || 'material', unidad: it.unidad || 'unidad', costo: String(it.costo ?? ''), acabado: it.acabado || '', familia: it.familia || '', codigo_corto: cortos[it.codigo]?.codigo || '', seccion: it.seccion || 'herraje', composicion: it.composicion || [] }); }}
                    data-testid={`cat-edit-${it.codigo}`}><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" className="h-7 px-1.5" title="Subir foto (aparece en la planilla de herrajes del PDF)"
                    onClick={() => { setFotoId(it.id); fotoRef.current?.click(); }} data-testid={`cat-subir-foto-${it.codigo}`}>📷</Button>
                  <Button variant="ghost" size="sm" className="h-7 px-1.5 text-red-400" onClick={() => borrar(it)} data-testid={`cat-del-${it.codigo}`}><Trash size={14} /></Button>
                </div>
              </div>
                  ))}
                  </div>
                </div>
              ))}
              </div>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// EDITOR
// =========================================================================
const Editor = ({ id }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const rolAdmin = ADMIN_LIKE.includes(role);
  const [saPerms, setSaPerms] = useState(null);
  useEffect(() => { api.get('/sa/mis-permisos').then((r) => setSaPerms(r.data)).catch(() => {}); }, []);
  const adminLike = saPerms ? saPerms.costos : rolAdmin;   // ver costos internos
  const cfgSA = saPerms ? saPerms.config : rolAdmin;       // utilidad / IVA / parámetros
  const adminSA = saPerms ? saPerms.admin : rolAdmin;      // aprobar / OT / editar aprobado
  const [p, setP] = useState(null);
  const [cfg, setCfg] = useState({});
  const [users, setUsers] = useState([]);
  const [elDialog, setElDialog] = useState(null);       // null | {} nuevo | elemento
  const [metodoChooser, setMetodoChooser] = useState(false);
  const [showFotoEd, setShowFotoEd] = useState(false);
  const [showAprobar, setShowAprobar] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [descTipo, setDescTipo] = useState('pct');
  const [descValor, setDescValor] = useState('');
  const [utilidadInput, setUtilidadInput] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [incluirDisenos, setIncluirDisenos] = useState(false);
  const [disSel, setDisSel] = useState(null); // null = todos los diseños
  const [showPrev, setShowPrev] = useState(false);
  const [prevUrl, setPrevUrl] = useState(null);

  const conDiseno = (p?.elementos || []).filter((e) => ['ventana2d', 'composicion'].includes(e.diseno?.tipo));
  const selActual = disSel ?? conDiseno.map((e) => e.codigo);
  const toggleDis = (c) => setDisSel(selActual.includes(c) ? selActual.filter((x) => x !== c) : [...selActual, c]);

  const descargarPdf = async () => {
    setPdfLoading(true);
    try {
      const conAnexos = incluirDisenos && selActual.length > 0;
      const params = { disenos: conAnexos ? 1 : 0 };
      if (conAnexos && selActual.length < conDiseno.length) params.items = selActual.join(',');
      const res = await api.get(`/sa/proyectos/${id}/pdf`, { params, responseType: 'blob', timeout: 60000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Presupuesto_${p?.numero || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('PDF generado');
    } catch (e) { toast.error('No se pudo generar el PDF'); }
    finally { setPdfLoading(false); }
  };

  const generarPrev = async (abrir = true) => {
    setPdfLoading(true);
    try {
      const conAnexos = incluirDisenos && selActual.length > 0;
      const params = { disenos: conAnexos ? 1 : 0 };
      if (conAnexos && selActual.length < conDiseno.length) params.items = selActual.join(',');
      const res = await api.get(`/sa/proyectos/${id}/pdf`, { params, responseType: 'blob', timeout: 60000 });
      setPrevUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); });
      if (abrir) setShowPrev(true);
    } catch (e) { toast.error('No se pudo generar la vista previa'); }
    finally { setPdfLoading(false); }
  };

  const quitarElementoPrev = async (el) => {
    try {
      await api.delete(`/sa/elementos/${el.id}`);
      toast.success(`${el.codigo} eliminado del presupuesto`);
      await load();
      generarPrev(false);
    } catch (e) { toast.error(errMsg(e, 'No se pudo eliminar')); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sa/proyectos/${id}`);
      setP(data);
      setDescTipo(data.descuento_tipo || 'pct');
      setDescValor(String(data.descuento_valor ?? ''));
      setUtilidadInput(String(data.utilidad_pct ?? ''));
    } catch (e) { toast.error('Presupuesto no encontrado'); navigate('/presupuestos-sa'); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/sa/config').then((r) => setCfg(r.data)).catch(() => {});
    if (adminSA) api.get('/users').then((r) => setUsers(r.data || [])).catch(() => {});
  }, [adminSA]);

  if (!p) return <div className="p-8 text-center text-[var(--text-muted)]">Cargando…</div>;
  const t = p.totales || {};
  const bloqueado = p.estado === 'aprobado' && !adminSA;

  const guardarBorrador = async () => {
    try {
      await api.put(`/sa/proyectos/${p.id}`, {
        nombre: p.nombre, cliente_nombre: p.cliente_nombre || '', cliente_telefono: p.cliente_telefono || '',
        direccion: p.direccion || '', vendor_id: p.vendor_id, notas: p.notas || '',
      });
      toast.success('Borrador guardado');
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };

  const cambiarEstado = async (estado) => {
    try {
      await api.post(`/sa/proyectos/${p.id}/estado`, { estado });
      toast.success(`Estado: ${ESTADOS[estado]?.label || estado}`);
      load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo cambiar el estado')); }
  };

  const aplicarDescuento = async () => {
    try {
      const { data } = await api.put(`/sa/proyectos/${p.id}/finanzas`, { descuento_tipo: descTipo, descuento_valor: Number(descValor || 0) });
      setP((prev) => ({ ...prev, ...data, elementos: prev.elementos }));
      toast.success('Descuento aplicado');
    } catch (e) { toast.error(errMsg(e, 'No se pudo aplicar el descuento')); }
  };

  const aplicarUtilidad = async () => {
    try {
      await api.put(`/sa/proyectos/${p.id}/finanzas`, { utilidad_pct: Number(utilidadInput || 0) });
      toast.success('Utilidad actualizada');
      load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo actualizar la utilidad')); }
  };

  const toggleIva = async (aplicar) => {
    setP((prev) => ({ ...prev, aplicar_iva: aplicar }));  // optimista
    try {
      const { data } = await api.put(`/sa/proyectos/${p.id}/finanzas`, { aplicar_iva: aplicar });
      setP((prev) => ({ ...prev, ...data, elementos: prev.elementos, aplicar_iva: aplicar }));
    } catch (e) {
      setP((prev) => ({ ...prev, aplicar_iva: !aplicar }));
      toast.error(errMsg(e, 'Solo administración modifica el IVA'));
    }
  };

  const duplicarElemento = async (el) => {
    try {
      await api.post(`/sa/proyectos/${p.id}/elementos`, {
        familia: el.familia, nombre: el.nombre ? `${el.nombre} (copia)` : '', cantidad: el.cantidad || 1,
        medidas: el.medidas || {}, variables: el.variables || {}, configuracion: el.configuracion || {},
        diseno: el.diseno || {}, notas: el.notas || '', descripcion_comercial: el.descripcion_comercial || '',
        despiece: (el.despiece || []).map(({ costo_total, costo_merma, cantidad_base, ...ln }) => ({ ...ln, cantidad: cantidad_base ?? ln.cantidad })),
        otros_costos: (el.otros_costos || []).map(({ costo_total, costo_merma, cantidad_base, ...ln }) => ({ ...ln, cantidad: cantidad_base ?? ln.cantidad })),
      });
      toast.success('Elemento duplicado');
      load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo duplicar')); }
  };

  const borrarElemento = (el) => {
    toast(`¿Eliminar ${el.codigo}?`, {
      action: { label: 'Eliminar', onClick: async () => {
        try { await api.delete(`/sa/elementos/${el.id}`); toast.success('Elemento eliminado'); load(); }
        catch (e) { toast.error(errMsg(e, 'Error')); }
      } },
      cancel: { label: 'Cancelar' },
    });
  };

  const medidasTxt = (m) => {
    if (!m) return '—';
    const a = m.ancho_m, h = m.alto_m;
    if (a && h) return `${a} × ${h} m`;
    return a ? `${a} m` : h ? `${h} m alto` : '—';
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="pre-editor">
      {/* ENCABEZADO */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-3 lg:justify-between">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/presupuestos-sa')} data-testid="pre-back"><ArrowLeft size={18} /></Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-black text-[var(--text)]">{p.numero}</h1>
              <EstadoBadge estado={p.estado} />
            </div>
            <p className="text-sm font-semibold text-[var(--text)] mt-0.5">{p.nombre}</p>
            <p className="text-xs text-[var(--text-muted)] flex items-center gap-1 flex-wrap">
              <User size={12} /> {p.cliente_nombre || 'Sin cliente'}{p.cliente_telefono ? ` · ${p.cliente_telefono}` : ''} · Vendedor: {p.vendor_name || '—'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="outline" size="sm" onClick={() => setShowInfo(true)} disabled={bloqueado} data-testid="pre-editar-info">
            <PencilSimple size={15} className="mr-1" /> Datos
          </Button>
          <Button variant="outline" size="sm" onClick={guardarBorrador} data-testid="pre-guardar-borrador">
            <FloppyDisk size={15} className="mr-1" /> Guardar borrador
          </Button>
          <Button variant="outline" size="sm" onClick={() => generarPrev(true)} disabled={pdfLoading || !t.num_elementos}
            className="text-[#0EA5E9] border-[#0EA5E9]/40 hover:bg-[#0EA5E9]/10" data-testid="pre-pdf-visualizar">
            <Eye size={15} className="mr-1" /> Visualizar
          </Button>
          <Button variant="outline" size="sm" onClick={descargarPdf} disabled={pdfLoading || !t.num_elementos}
            className="text-red-500 border-red-500/40 hover:bg-red-500/10" data-testid="pre-pdf-btn">
            <FilePdf size={15} className="mr-1" /> {pdfLoading ? 'Generando…' : 'PDF'}
          </Button>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--text-muted)] cursor-pointer select-none">
            <input type="checkbox" checked={incluirDisenos} onChange={(e) => setIncluirDisenos(e.target.checked)}
              className="accent-[#0EA5E9] w-3.5 h-3.5" data-testid="pre-pdf-disenos-check" />
            Incluir diseños técnicos
          </label>
          {incluirDisenos && conDiseno.length > 0 && (
            <div className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 pl-1" data-testid="pre-pdf-disenos-lista">
              <span className="text-[10px] font-black uppercase tracking-wide text-[#0EA5E9]">Diseños a incluir:</span>
              {(p.elementos || []).map((e, i) => (
                ['ventana2d', 'composicion'].includes(e.diseno?.tipo) ? (
                  <label key={e.codigo} className="flex items-center gap-1 text-[11px] text-[var(--text-muted)] cursor-pointer select-none">
                    <input type="checkbox" checked={selActual.includes(e.codigo)} onChange={() => toggleDis(e.codigo)}
                      className="accent-[#0EA5E9] w-3 h-3" data-testid={`pre-pdf-dis-${e.codigo}`} />
                    Ítem {String(i + 1).padStart(2, '0')} — {e.codigo} {e.nombre || ''}
                  </label>
                ) : null
              ))}
              {selActual.length === 0 && <span className="text-[10px] text-amber-500">Sin diseños seleccionados: el PDF saldrá solo comercial</span>}
            </div>
          )}
          {incluirDisenos && conDiseno.length === 0 && (
            <span className="text-[10px] text-amber-500" data-testid="pre-pdf-sin-disenos">⚠️ Ningún elemento tiene un diseño 2D guardado.</span>
          )}
          {['borrador', 'en_calculo'].includes(p.estado) && (
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!t.num_elementos}
              onClick={() => cambiarEstado('generado')} data-testid="pre-generar-btn">
              Generar presupuesto
            </Button>
          )}
          {p.estado === 'generado' && (
            <Button size="sm" variant="outline" onClick={() => cambiarEstado('enviado')} data-testid="pre-enviar-btn">Marcar enviado</Button>
          )}
          {p.estado === 'enviado' && (
            <Button size="sm" variant="outline" onClick={() => cambiarEstado('en_negociacion')} data-testid="pre-negociacion-btn">En negociación</Button>
          )}
          {adminSA && p.estado !== 'aprobado' && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!t.num_elementos}
              onClick={() => setShowAprobar(true)} data-testid="pre-aprobar-btn">
              <CheckCircle size={15} className="mr-1" /> Aprobar presupuesto
            </Button>
          )}
          {!['aprobado', 'cancelado', 'rechazado'].includes(p.estado) && (
            <Select value="" onValueChange={(v) => v && cambiarEstado(v)}>
              <SelectTrigger className="w-[110px] h-9 text-xs" data-testid="pre-mas-estados"><SelectValue placeholder="Más…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rechazado">Rechazado</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
                <SelectItem value="borrador">Volver a borrador</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* OT asociada */}
      {p.ot_id ? (
        <Card className="p-3 border-emerald-500/40 bg-emerald-500/10 flex items-center justify-between flex-wrap gap-2" data-testid="pre-ot-card">
          <p className="text-sm font-semibold text-emerald-500 flex items-center gap-2">
            <Hammer size={18} /> Este presupuesto ya tiene una OT asociada: <b>{p.ot_numero || p.ot_id}</b>
          </p>
          <Button size="sm" variant="outline" onClick={() => navigate('/produccion')} data-testid="pre-abrir-ot">
            Abrir OT <CaretRight size={14} />
          </Button>
        </Card>
      ) : p.estado === 'aprobado' && adminSA ? (
        <Card className="p-3 border-amber-500/40 bg-amber-500/10 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-amber-500 font-semibold">Presupuesto aprobado sin OT.</p>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowAprobar(true)} data-testid="pre-generar-ot-btn">
            <Hammer size={15} className="mr-1" /> Generar OT
          </Button>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ELEMENTOS */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-[var(--text)]">Elementos del proyecto ({t.num_elementos || 0})</h2>
            <Button size="sm" onClick={() => setMetodoChooser(true)} disabled={bloqueado || p.estado === 'aprobado'}
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="pre-add-elemento">
              <Plus size={15} className="mr-1" /> Agregar diseño
            </Button>
          </div>
          {metodoChooser && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="metodo-chooser" onClick={() => setMetodoChooser(false)}>
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl w-full max-w-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                <p className="text-base font-black text-[var(--text)]">¿Cómo quieres crear el diseño?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { k: 'foto', ico: '📷', tit: 'Desde imagen', desc: 'Sube la foto de tu solicitud o plano y la IA arma el borrador completo (crea un presupuesto nuevo).' },
                    { k: '2d', ico: '📐', tit: 'Modulador / Diseñador 2D', desc: 'Dibuja el elemento con módulos, cotas, uniones y símbolos (duchas usan el Modulador).' },
                    { k: 'lego', ico: '🧱', tit: 'Lego — Formato Solicitud', desc: 'Inserta elementos y herrajes del catálogo con números y leyenda automática, como tu hoja.' },
                    { k: 'manual', ico: '✍️', tit: 'Manual', desc: 'Llena familia, medidas y materiales directamente, sin dibujo.' },
                  ].map((op) => (
                    <button key={op.k} type="button" data-testid={`metodo-${op.k}`}
                      onClick={() => { setMetodoChooser(false); if (op.k === 'foto') setShowFotoEd(true); else setElDialog({ metodo: op.k }); }}
                      className="text-left border border-[var(--border)] rounded-lg p-3 hover:border-[#0EA5E9] hover:bg-[#0EA5E9]/5">
                      <p className="text-sm font-black text-[var(--text)]">{op.ico} {op.tit}</p>
                      <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{op.desc}</p>
                    </button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="h-8 text-xs w-full" onClick={() => setMetodoChooser(false)}>Cancelar</Button>
              </div>
            </div>
          )}
          {showFotoEd && <CrearDesdeFoto open onClose={() => setShowFotoEd(false)}
            onCreated={(np) => { setShowFotoEd(false); navigate(`/presupuestos-sa/${np.id}`); }} />}
          {(p.elementos || []).length === 0 ? (
            <Card className="p-8 text-center text-sm text-[var(--text-muted)]">
              Sin elementos. Agrega ventanas, puertas, fachadas, barandas… con sus medidas y despiece.
            </Card>
          ) : (p.elementos || []).map((el) => (
            <Card key={el.id} className="p-3" data-testid={`pre-elemento-${el.codigo}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-bold text-[var(--text)]">
                    <span className="text-[#0EA5E9] mr-2">{el.codigo}</span>
                    {el.nombre || famLabel(el.familia)}
                    <span className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--text-muted)]">{famLabel(el.familia)}</span>
                    {el.regla_tecnica_pendiente && !(el.despiece || []).length && (
                      <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500" data-testid={`pre-el-pendiente-${el.codigo}`}>Regla técnica pendiente</span>
                    )}
                  </p>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    Medidas: {medidasTxt(el.medidas)}{el.area_calculada != null ? ` · Superficie: ${el.area_calculada} ` : ''} · Cantidad: {el.cantidad}
                    {el.notas ? ` · ${el.notas}` : ''}
                  </p>
                  {Object.keys(el.variables || {}).length > 0 && (
                    <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
                      {Object.entries(el.variables).filter(([, v]) => v !== '' && v != null).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join('/') : v}`).join(' · ')}
                    </p>
                  )}
                  {el.diseno?.tipo === 'ventana2d' && (
                    <div className="mt-2 flex items-center gap-2">
                      <PreviewDiseno diseno={el.diseno} height={110} />
                      <span className="text-[10px] text-[var(--text-muted)]">Diseño v{el.diseno.version || 0}<br />{el.diseno.cols_mm?.length || 1}×{el.diseno.rows_mm?.length || 1} módulos</span>
                    </div>
                  )}
                  {((el.despiece || []).length > 0 || (el.despiece_auto || []).length > 0) && (
                    <div className="mt-2 border border-[var(--border)] rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[var(--bg)] text-[var(--text-muted)] text-left">
                            <th className="px-2 py-1">Componente</th>
                            <th className="px-2 py-1 text-right">Cant.</th>
                            <th className="px-2 py-1">Unidad</th>
                            {adminLike && <th className="px-2 py-1 text-right">Costo unit.</th>}
                            {adminLike && <th className="px-2 py-1 text-right">Costo total</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(el.despiece || []).concat(el.despiece_auto || []).concat(adminLike ? (el.otros_costos || []) : []).map((ln, i) => (
                            <tr key={i} className="border-t border-[var(--border)]">
                              <td className="px-2 py-1">
                                {ln.origen === 'regla' && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-500 mr-1" title={ln.regla_nombre}>AUTO</span>}
                                {ln.item_codigo ? <b className="text-[#0EA5E9] mr-1">{ln.item_codigo}</b> : null}{ln.descripcion}
                                {ln.merma_pct ? <span className="text-[9px] text-[var(--text-muted)] ml-1">(merma {ln.merma_pct}%)</span> : null}
                                {ln.error ? <span className="text-[9px] text-amber-500 ml-1">⚠ {ln.error}</span> : null}
                              </td>
                              <td className="px-2 py-1 text-right">{ln.cantidad}</td>
                              <td className="px-2 py-1">{ln.unidad}</td>
                              {adminLike && <td className="px-2 py-1 text-right">{Q(ln.costo_unitario)}</td>}
                              {adminLike && <td className="px-2 py-1 text-right font-semibold">{Q(ln.costo_total)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="text-right space-y-1 min-w-[130px]">
                  {'precio_unitario' in el && <p className="text-xs text-[var(--text-muted)]">Precio: <b className="text-[var(--text)]">{Q(el.precio_unitario)}</b></p>}
                  {'precio_venta' in el && <p className="text-sm font-bold text-[var(--text)]">Subtotal: {Q(el.precio_venta)}</p>}
                  {adminLike && (
                    <p className="text-[11px] text-[var(--text-muted)]">
                      Costo {Q(el.costo_total)} · Utilidad {Q(el.utilidad)}<br />
                      Markup {el.markup_pct}% · Margen {el.margen_pct}%
                    </p>
                  )}
                  <div className="flex justify-end gap-1 pt-1">
                    <Button variant="ghost" size="sm" className="h-7 px-2" disabled={p.estado === 'aprobado'}
                      onClick={() => duplicarElemento(el)} title="Duplicar diseño — copia editable e independiente (cambia medidas, módulos, vidrio, herrajes)" data-testid={`pre-el-dup-${el.codigo}`}><Copy size={14} /></Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2" disabled={p.estado === 'aprobado'}
                      onClick={() => setElDialog(el)} data-testid={`pre-el-edit-${el.codigo}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" disabled={p.estado === 'aprobado'}
                      onClick={() => borrarElemento(el)} data-testid={`pre-el-del-${el.codigo}`}><Trash size={14} /></Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {/* COSTOS DE EJECUCIÓN */}
          <EjecucionCard key={`ej-${p.updated_at}`} proyecto={p} adminLike={adminLike} bloqueado={bloqueado || p.estado === 'aprobado'} onSaved={load} />

          {/* COSTEO INTERNO (solo administración) */}
          {adminLike && <CosteoInterno proyectoId={p.id} refreshKey={p.updated_at} />}
        </div>

        {/* RESUMEN FINANCIERO */}
        <div className="space-y-3">
          <Card className="p-4 space-y-2" data-testid="pre-resumen">
            <h3 className="text-sm font-black uppercase tracking-wide text-[var(--text-muted)]"><span className="md:hidden text-[#0EA5E9]">Paso 7 — </span>Resumen financiero</h3>
            {adminLike && (
              <div className="border border-[var(--border)] rounded-lg p-2.5 space-y-1 text-sm bg-[var(--bg)]" data-testid="pre-resumen-interno">
                <p className="text-[10px] font-bold uppercase text-amber-500">Interno — solo administración</p>
                <Row l="Materiales" v={Q(t.costo_materiales)} muted />
                <Row l="Mano de obra" v={Q(t.costo_mano_obra)} muted />
                <Row l="Ejecución (logística)" v={Q(t.costo_ejecucion)} muted />
                <Row l="Otros costos" v={Q(t.otros_costos)} muted />
                <Row l="Costo del proyecto" v={Q(t.costo_total)} bold />
                <Row l={`Utilidad (${p.metodo_utilidad === 'margen' ? 'margen s/precio' : 'markup s/costo'} ${p.utilidad_pct}%)`} v={Q(t.utilidad)} />
                <Row l="Utilidad después del descuento" v={Q(t.utilidad_final)} bold />
                <Row l="Margen real" v={`${t.margen_real_pct ?? 0}%`} muted />
                <Row l="Markup real" v={`${t.markup_real_pct ?? 0}%`} muted />
                <div className="flex gap-1.5 pt-1">
                  <Input type="number" value={utilidadInput} onChange={(e) => setUtilidadInput(e.target.value)} className="h-8 text-xs" placeholder="Utilidad %" data-testid="fin-utilidad-input" disabled={!cfgSA} />
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={aplicarUtilidad} disabled={bloqueado || !cfgSA} data-testid="fin-utilidad-aplicar">Aplicar</Button>
                </div>
              </div>
            )}
            <div className="space-y-1 text-sm pt-1">
              <Row l="Precio de venta" v={Q(t.precio_base)} />
              <Row l={`Descuento${p.descuento_tipo === 'pct' && p.descuento_valor ? ` (${p.descuento_valor}%)` : ''}`} v={`− ${Q(t.descuento_monto)}`} />
              <Row l="Base imponible" v={Q(t.base_imponible)} />
              <Row l={`IVA ${p.aplicar_iva !== false ? (t.iva_pct || 12) + '%' : '(sin IVA)'}`} v={Q(t.iva_monto)} />
              <div className="border-t border-[var(--border)] pt-1.5 flex justify-between items-center">
                <span className="font-black text-[var(--text)]">TOTAL</span>
                <span className="font-black text-lg text-[#0EA5E9]" data-testid="pre-total">{Q(t.total)}</span>
              </div>
            </div>
          </Card>

          {/* Descuento */}
          <Card className="p-4 space-y-2">
            <h3 className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Descuento</h3>
            <div className="flex gap-1.5">
              <Select value={descTipo} onValueChange={setDescTipo}>
                <SelectTrigger className="w-[70px] h-9" data-testid="fin-desc-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pct">%</SelectItem>
                  <SelectItem value="monto">Q</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" min="0" value={descValor} onChange={(e) => setDescValor(e.target.value)} className="h-9" placeholder="0" data-testid="fin-descuento-input" />
              <Button size="sm" className="h-9 bg-[#0EA5E9] hover:bg-[#0284C7] text-white" onClick={aplicarDescuento} disabled={bloqueado} data-testid="fin-aplicar-descuento">Aplicar</Button>
            </div>
            {!cfgSA && <p className="text-[11px] text-[var(--text-muted)]">Tu límite autorizado: {cfg.descuento_max_vendedor_pct ?? 5}%. Descuentos mayores requieren autorización de gerencia.</p>}
            <label className={`flex items-center gap-2 text-sm pt-1 ${cfgSA ? 'cursor-pointer' : 'opacity-60'}`}>
              <input type="checkbox" checked={p.aplicar_iva !== false} disabled={!cfgSA || bloqueado}
                onChange={(e) => toggleIva(e.target.checked)} data-testid="fin-iva-check" className="accent-[#0EA5E9] w-4 h-4" />
              Aplicar IVA {t.iva_pct || cfg.iva_pct || 12}%
            </label>
          </Card>

          {/* VISTA PREVIA PDF — editable antes de exportar (móvil y escritorio) */}
      {showPrev && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4" data-testid="pre-pdf-preview">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-[var(--border)]">
              <p className="text-sm font-black text-[var(--text)]">Vista previa — {p.numero}</p>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => prevUrl && window.open(prevUrl, '_blank')} data-testid="prev-abrir">Abrir en pestaña</Button>
                <Button size="sm" className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={descargarPdf} disabled={pdfLoading} data-testid="prev-descargar"><FilePdf size={13} className="mr-1" />Exportar / Guardar PDF</Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowPrev(false)} data-testid="prev-cerrar">✕ Cerrar</Button>
              </div>
            </div>
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
              <div className="lg:w-[300px] shrink-0 border-b lg:border-b-0 lg:border-r border-[var(--border)] overflow-y-auto p-2 space-y-1.5 max-h-[30vh] lg:max-h-none" data-testid="prev-elementos">
                <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">Elementos del presupuesto — edita aquí y la vista se actualiza</p>
                {(p.elementos || []).map((e) => (
                  <div key={e.id} className="flex items-center gap-1.5 border border-[var(--border)] rounded-md px-2 py-1.5" data-testid={`prev-el-${e.codigo}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-[var(--text)] truncate">{e.codigo} · {e.nombre || FAMILIAS.find((x) => x.v === e.familia)?.label || e.familia}</p>
                      <p className="text-[10px] text-[var(--text-muted)]">Cant. {e.cantidad || 1} · Q{Number(e.precio_venta || 0).toFixed(2)}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 px-1.5" title="Duplicar" onClick={async () => { await duplicarElemento(e); generarPrev(false); }} data-testid={`prev-dup-${e.codigo}`}><Copy size={13} /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-1.5 text-red-400" title="Eliminar" onClick={() => quitarElementoPrev(e)} data-testid={`prev-del-${e.codigo}`}><Trash size={13} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="h-8 text-xs w-full" onClick={() => { setShowPrev(false); setMetodoChooser(true); }} data-testid="prev-agregar-el"><Plus size={13} className="mr-1" />Agregar elemento</Button>
                <p className="text-[9px] text-[var(--text-muted)]">Total: Q{Number(t.total || 0).toFixed(2)}{pdfLoading ? ' · Actualizando vista…' : ''}</p>
              </div>
              <div className="flex-1 bg-[var(--bg)]">
                {prevUrl ? (
                  <iframe src={prevUrl} title="Vista previa PDF" className="w-full h-full min-h-[300px]" data-testid="prev-iframe" />
                ) : <p className="p-6 text-sm text-[var(--text-muted)]">Generando…</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Condiciones comerciales (PDF) */}
          <CondicionesCard proyecto={p} bloqueado={bloqueado} onSaved={load} />
        </div>
      </div>

      {elDialog !== null && (
        <ElementoDialog proyecto={p} elemento={elDialog.id ? elDialog : null} adminLike={adminLike} metodo={elDialog.metodo}
          onClose={() => setElDialog(null)} onSaved={() => { setElDialog(null); load(); }} />
      )}
      {showAprobar && (
        <AprobarDialog proyecto={p} onClose={() => setShowAprobar(false)}
          onDone={() => { setShowAprobar(false); load(); }} />
      )}
      {showInfo && (
        <InfoDialog proyecto={p} users={users} adminLike={adminLike} onClose={() => setShowInfo(false)}
          onSaved={() => { setShowInfo(false); load(); }} />
      )}
    </div>
  );
};

const Row = ({ l, v, bold, muted }) => (
  <div className={`flex justify-between ${muted ? 'text-[var(--text-muted)] text-xs' : ''}`}>
    <span className={bold ? 'font-bold' : ''}>{l}</span>
    <span className={`${bold ? 'font-bold' : 'font-semibold'} text-right`}>{v}</span>
  </div>
);

// =========================================================================
// EDITAR DATOS DEL PROYECTO
// =========================================================================
const InfoDialog = ({ proyecto, users, adminLike, onClose, onSaved }) => {
  const [f, setF] = useState({
    nombre: proyecto.nombre || '', cliente_nombre: proyecto.cliente_nombre || '',
    cliente_telefono: proyecto.cliente_telefono || '', direccion: proyecto.direccion || '',
    vendor_id: proyecto.vendor_id || '', notas: proyecto.notas || '',
  });
  const guardar = async () => {
    try {
      await api.put(`/sa/proyectos/${proyecto.id}`, f);
      toast.success('Datos guardados');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader><DialogTitle>Datos del presupuesto {proyecto.numero}</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Proyecto" data-testid="info-nombre" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.cliente_nombre} onChange={(e) => setF({ ...f, cliente_nombre: e.target.value })} placeholder="Cliente" data-testid="info-cliente" />
            <Input value={f.cliente_telefono} onChange={(e) => setF({ ...f, cliente_telefono: e.target.value })} placeholder="Teléfono" data-testid="info-telefono" />
          </div>
          <Input value={f.direccion} onChange={(e) => setF({ ...f, direccion: e.target.value })} placeholder="Dirección del proyecto" data-testid="info-direccion" />
          {adminLike && (
            <Select value={f.vendor_id} onValueChange={(v) => setF({ ...f, vendor_id: v })}>
              <SelectTrigger data-testid="info-vendedor"><SelectValue placeholder="Vendedor" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Textarea rows={2} value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} placeholder="Notas" data-testid="info-notas" />
          <Button onClick={guardar} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="info-guardar">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// ELEMENTO (crear / editar) con despiece
// =========================================================================
const lineaVacia = () => ({ item_codigo: '', descripcion: '', cantidad: '', unidad: 'unidad', costo_unitario: '', tipo: 'material', categoria: '', merma_pct: '' });

const CATEGORIAS_COSTO = [
  ['', 'Categoría (auto)'], ['perfiles', 'Perfiles'], ['vidrio', 'Vidrio'], ['herrajes', 'Herrajes'],
  ['accesorios', 'Accesorios'], ['consumibles', 'Consumibles'], ['mano_obra', 'Mano de obra'], ['otros', 'Otros'],
];

const ElementoDialog = ({ proyecto, elemento, adminLike, metodo, onClose, onSaved }) => {
  const [f, setF] = useState(() => elemento ? {
    familia: elemento.familia, nombre: elemento.nombre || '',
    descripcion_comercial: elemento.descripcion_comercial || '',
    ancho_m: elemento.medidas?.ancho_m || '', alto_m: elemento.medidas?.alto_m || '',
    cantidad: elemento.cantidad || 1, notas: elemento.notas || '',
    variables: { ...(elemento.variables || {}) },
    diseno: ['ventana2d', 'composicion'].includes(elemento.diseno?.tipo) ? { ...elemento.diseno } : null,
    despiece: (elemento.despiece || []).map((l) => ({ ...lineaVacia(), ...l, cantidad: l.cantidad_base ?? l.cantidad })),
    otros_costos: (elemento.otros_costos || []).map((l) => ({ ...lineaVacia(), ...l, tipo: 'otro', cantidad: l.cantidad_base ?? l.cantidad })),
    receta: elemento.receta || {},
  } : { familia: 'ventanas', nombre: '', descripcion_comercial: '', ancho_m: '', alto_m: '', cantidad: 1, notas: '', variables: {}, diseno: metodo === 'lego' ? composicionDefault() : metodo === '2d' ? { ...disenoDefault() } : null, despiece: [lineaVacia()], otros_costos: [], receta: {} });
  const [catalogo, setCatalogo] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [saving, setSaving] = useState(false);
  // Asistente por pasos (solo móvil) — mismo estado `f`, sin duplicar lógica
  const wizKey = `sa_wiz_${elemento?.id || 'nuevo'}`;
  const [esMovil, setEsMovil] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  const [paso, setPaso] = useState(() => (elemento?.id ? Math.min(5, Math.max(1, Number(localStorage.getItem(wizKey)) || 1)) : 1));
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const h = (e) => setEsMovil(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  useEffect(() => { if (elemento?.id) localStorage.setItem(wizKey, String(paso)); }, [paso, wizKey, elemento?.id]);
  const vis = (n) => (!esMovil || paso === n ? '' : 'hidden');
  const TITULOS_PASO = ['Datos del elemento', 'Diseño / Modulación', 'Características técnicas', 'Uniones y herrajes', 'Materiales y cantidades'];
  const DESC_PASO = ['Identifica la solución: tipo, nombre y medidas generales.',
    'Arma visualmente la solución y define cómo se divide.',
    'Define vidrio, espesor, color y acabados.',
    'Define cómo se unen los vidrios y qué herrajes llevará.',
    'Revisa los materiales que necesita esta solución.'];
  const seccionReceta = esMovil ? ({ 2: 'geometria', 3: 'tecnica', 4: 'uniones', 5: 'pendientes' }[paso] || 'geometria') : null;

  useEffect(() => { api.get('/sa/catalogo').then((r) => setCatalogo(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { api.get('/sa/plantillas').then((r) => setPlantillas(r.data.plantillas || [])).catch(() => {}); }, []);

  const aplicarPlantilla = (tid) => {
    const t = plantillas.find((x) => x.id === tid);
    if (!t) return;
    const dis = ['ventana2d', 'composicion'].includes(t.diseno?.tipo)
      ? { ...t.diseno, version: f.diseno?.version || 0 }
      : f.diseno;
    setF({
      ...f, familia: t.familia || f.familia, nombre: f.nombre || t.nombre,
      variables: { ...(t.variables || {}), ...f.variables }, diseno: dis,
    });
    toast.success(`Plantilla "${t.nombre}" aplicada — ajusta medidas, divisiones y aperturas libremente`);
  };

  const guardarPlantilla = async () => {
    const nombre = window.prompt('Nombre de la plantilla:', f.nombre || '');
    if (!nombre) return;
    try {
      await api.post('/sa/plantillas', { nombre, familia: f.familia, diseno: f.diseno || {}, variables: f.variables || {}, notas: '' });
      const r = await api.get('/sa/plantillas');
      setPlantillas(r.data.plantillas || []);
      toast.success('Plantilla guardada');
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar la plantilla')); }
  };

  const setLinea = (key, i, campo, val) => {
    const arr = [...f[key]];
    arr[i] = { ...arr[i], [campo]: val };
    if (campo === 'item_codigo' && val) {
      const cat = catalogo.find((c) => c.codigo === val);
      if (cat) arr[i] = { ...arr[i], descripcion: cat.nombre, unidad: cat.unidad, tipo: cat.tipo, costo_unitario: adminLike ? cat.costo : 0 };
    }
    setF({ ...f, [key]: arr });
  };

  const guardar = async () => {
    if (f.diseno) {
      const sc = (f.diseno.cols_mm || []).reduce((a, b) => a + Number(b || 0), 0);
      const sr = (f.diseno.rows_mm || []).reduce((a, b) => a + Number(b || 0), 0);
      if (Math.abs(sc - f.diseno.ancho_mm) > 0.51 || Math.abs(sr - f.diseno.alto_mm) > 0.51) {
        return toast.error('Las divisiones del diseño no coinciden con la medida total. Corrígelas antes de guardar.');
      }
    }
    setSaving(true);
    try {
      const body = {
        familia: f.familia, nombre: f.nombre, descripcion_comercial: f.descripcion_comercial || '',
        cantidad: Number(f.cantidad || 1), notas: f.notas, receta: f.receta || {},
        medidas: { ...(f.ancho_m ? { ancho_m: Number(f.ancho_m) } : {}), ...(f.alto_m ? { alto_m: Number(f.alto_m) } : {}) },
        variables: f.variables,
        // MODELO ÚNICO: para PABM/VTF el diseño SIEMPRE se regenera desde la receta (nunca queda congelado un diseño antiguo)
        diseno: (['pabm', 'vtf'].includes(f.familia) && (f.receta?.vano_ancho || f.receta?.fijos?.length))
          ? derivarDiseno(f.receta, f.familia) : (f.diseno || {}),
        despiece: f.despiece.filter((l) => l.descripcion || l.item_codigo).map((l) => ({
          ...l, cantidad: Number(l.cantidad || 0), merma_pct: Number(l.merma_pct || 0),
          costo_unitario: l.item_codigo && !adminLike ? 0 : Number(l.costo_unitario || 0),
        })),
        otros_costos: (f.otros_costos || []).filter((l) => l.descripcion).map((l) => ({ ...l, tipo: 'otro', cantidad: Number(l.cantidad || 0), merma_pct: Number(l.merma_pct || 0), costo_unitario: Number(l.costo_unitario || 0) })),
      };
      if (elemento) await api.put(`/sa/elementos/${elemento.id}`, { ...body, codigo: elemento.codigo });
      else {
        const resp = await api.post(`/sa/proyectos/${proyecto.id}/elementos`, body);
        if (resp?.data?.id) localStorage.setItem(`sa_wiz_${resp.data.id}`, String(paso));  // reanudar en el mismo paso
      }
      localStorage.removeItem('sa_wiz_nuevo');
      toast.success(elemento ? 'Elemento actualizado' : 'Elemento agregado');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar el elemento')); }
    finally { setSaving(false); }
  };
  const lineasEditor = ({ keyName, titulo, conTipo }) => (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">{titulo}</p>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
          onClick={() => setF({ ...f, [keyName]: [...f[keyName], { ...lineaVacia(), ...(keyName === 'otros_costos' ? { tipo: 'otro' } : {}) }] })}
          data-testid={`el-add-linea-${keyName}`}>
          <Plus size={13} className="mr-0.5" /> Línea
        </Button>
      </div>
      <div className="space-y-1.5 mt-1">
        {f[keyName].map((ln, i) => (
          <div key={i}>
          <div className="grid grid-cols-12 gap-1 items-center max-md:grid-cols-6 max-md:gap-1.5 max-md:border max-md:border-[var(--border)] max-md:rounded-lg max-md:p-2 max-md:mb-1.5">
            {keyName === 'despiece' && (
              <select value={ln.item_codigo || ''} onChange={(e) => setLinea(keyName, i, 'item_codigo', e.target.value)}
                className="col-span-3 h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1"
                data-testid={`el-linea-cat-${i}`}>
                <option value="">Manual…</option>
                {catalogo.map((c) => <option key={c.codigo} value={c.codigo}>{`${c.codigo} — ${c.nombre}`}</option>)}
              </select>
            )}
            <Input value={ln.descripcion} onChange={(e) => setLinea(keyName, i, 'descripcion', e.target.value)} placeholder="Descripción"
              className={`${keyName === 'despiece' ? (adminLike ? 'col-span-3' : 'col-span-5') : (adminLike ? 'col-span-6' : 'col-span-8')} h-8 text-xs`} data-testid={`el-linea-desc-${keyName}-${i}`} />
            <Input type="number" value={ln.cantidad} onChange={(e) => setLinea(keyName, i, 'cantidad', e.target.value)} placeholder="Cant."
              className="col-span-2 h-8 text-xs" data-testid={`el-linea-cant-${keyName}-${i}`} />
            <Input value={ln.unidad} onChange={(e) => setLinea(keyName, i, 'unidad', e.target.value)} placeholder="Unidad"
              className={`${adminLike ? 'col-span-1' : 'col-span-3'} h-8 text-xs`} />
            {adminLike && (
              <Input type="number" value={ln.costo_unitario} onChange={(e) => setLinea(keyName, i, 'costo_unitario', e.target.value)} placeholder="Costo Q"
                className="col-span-2 h-8 text-xs" data-testid={`el-linea-costo-${keyName}-${i}`} />
            )}
            <button type="button" className="col-span-1 text-red-400 flex justify-center"
              onClick={() => setF({ ...f, [keyName]: f[keyName].filter((_, j) => j !== i) })} data-testid={`el-linea-del-${keyName}-${i}`}>
              <X size={14} />
            </button>
          </div>
          {adminLike && keyName === 'despiece' && (ln.descripcion || ln.item_codigo) && (
            <div className="grid grid-cols-12 gap-1 items-center mt-0.5 mb-1 max-md:grid-cols-6 max-md:gap-1.5 max-md:border max-md:border-t-0 max-md:border-[var(--border)] max-md:rounded-b-lg max-md:p-2 max-md:-mt-3 max-md:mb-1.5">
              <select value={ln.categoria || ''} onChange={(e) => setLinea(keyName, i, 'categoria', e.target.value)}
                className="col-span-3 h-7 text-[11px] border border-dashed border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1"
                data-testid={`el-linea-categoria-${i}`}>
                {CATEGORIAS_COSTO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Input type="number" min="0" value={ln.merma_pct} onChange={(e) => setLinea(keyName, i, 'merma_pct', e.target.value)}
                placeholder="Merma %" className="col-span-2 h-7 text-[11px] border-dashed" data-testid={`el-linea-merma-${i}`} />
              <span className="col-span-7 text-[10px] text-[var(--text-muted)]">
                {Number(ln.merma_pct) > 0 && Number(ln.cantidad) > 0
                  ? `Cant. final con merma: ${(Number(ln.cantidad) * (1 + Number(ln.merma_pct) / 100)).toFixed(2)} ${ln.unidad || ''}`
                  : 'Merma % (opcional)'}
              </span>
            </div>
          )}
          </div>
        ))}
        {conTipo && adminLike && keyName === 'despiece' && (
          <p className="text-[10px] text-[var(--text-muted)]">Elige un item del catálogo (toma costo/unidad automáticamente) o escribe la línea manual. El tipo (material/M.O.) viene del catálogo.</p>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[640px] lg:max-w-[960px] max-h-[92vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>{elemento ? `Editar ${elemento.codigo}` : 'Agregar elemento'}</DialogTitle>
          <DialogDescription className="sr-only">Editor de elemento del presupuesto</DialogDescription>
        </DialogHeader>
        <div className="lg:grid lg:grid-cols-[1fr_230px] lg:gap-4 lg:items-start">
        <div className="min-w-0">
        {esMovil && (
          <div className="sticky top-0 z-10 bg-[var(--card)] pb-2 border-b border-[var(--border)] space-y-1.5" data-testid="wiz-bar">
            <p className="text-[11px] font-bold text-[#0EA5E9] truncate" data-testid="wiz-resumen">
              {elemento?.codigo || 'NUEVO'} · {f.nombre || FAMILIAS.find((x) => x.v === f.familia)?.label || ''} · {(f.diseno ? (dimsDiseno(f.diseno).ancho_mm / 1000).toFixed(3) : f.ancho_m) || '—'} × {(f.diseno ? (dimsDiseno(f.diseno).alto_mm / 1000).toFixed(3) : f.alto_m) || '—'} m
            </p>
            <div className="h-1.5 rounded bg-[var(--border)]"><div className="h-1.5 rounded bg-[#0EA5E9] transition-all" style={{ width: `${(paso / 5) * 100}%` }} data-testid="wiz-progreso" /></div>
            <div className="flex items-center justify-between gap-2">
              <Button type="button" variant="outline" size="sm" className="h-10 px-3 text-xs" disabled={paso === 1} onClick={() => setPaso(paso - 1)} data-testid="wiz-anterior">← Anterior</Button>
              <span className="text-[10px] font-bold text-center text-[var(--text-muted)]" data-testid="wiz-paso-label">Paso {paso} de 5<br />{TITULOS_PASO[paso - 1]}</span>              <Button type="button" size="sm" className="h-10 px-3 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white" disabled={paso === 5} onClick={() => setPaso(paso + 1)} data-testid="wiz-siguiente">Siguiente →</Button>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] text-center" data-testid="wiz-paso-desc">{DESC_PASO[paso - 1]}</p>
          </div>
        )}
        {!esMovil && (
          <p className="text-[11px] text-[var(--text-muted)] -mt-2" data-testid="el-desc-general">Configura la solución paso a paso: datos, diseño, características, uniones/herrajes y materiales.</p>
        )}
        <div className="space-y-3 min-w-0">
          <div className={`flex gap-2 ${vis(1)}`}>
            {[['pabm', 'PUERTA ABATIBLE + FIJOS', 'PABM'], ['vtf', 'VIDRIO FIJO DUCHA', 'VTF']].map(([v, l, s]) => (
              <button key={v} type="button" onClick={() => setF({ ...f, familia: v })} data-testid={`el-tipo-card-${v}`}
                className={`flex-1 rounded-lg border-2 px-2 py-2 text-left transition-colors ${f.familia === v ? 'border-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] hover:border-[#0EA5E9]/50'}`}>
                <span className="block text-sm font-black text-[#0EA5E9]">{s}</span>
                <span className="block text-[10px] font-bold text-[var(--text-muted)]">{l}</span>
              </button>
            ))}
          </div>
          <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 ${vis(1)}`}>
            <div className="col-span-1">
              <label className="text-xs font-bold text-[var(--text-muted)]">Tipo</label>
              <Select value={f.familia} onValueChange={(v) => setF({ ...f, familia: v })}>
                <SelectTrigger className="h-9" data-testid="el-familia"><SelectValue /></SelectTrigger>
                <SelectContent>{FAMILIAS.map((x) => <SelectItem key={x.v} value={x.v}>{x.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1 sm:col-span-3">
              <label className="text-xs font-bold text-[var(--text-muted)]">Descripción</label>
              <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Ej. Ventana corrediza 2 hojas" className="h-9" data-testid="el-nombre" />
            </div>
          </div>
          <div className={vis(1)}>
            <label className="text-xs font-bold text-[var(--text-muted)]">Descripción comercial (aparece en el PDF — vacío = automática)</label>
            <Textarea rows={2} value={f.descripcion_comercial} onChange={(e) => setF({ ...f, descripcion_comercial: e.target.value })}
              placeholder="Texto que verá el cliente en la cotización…" className="text-xs min-h-[44px]" data-testid="el-desc-comercial" />
          </div>
          {['pabm', 'vtf'].includes(f.familia) && (
            <div className={`min-w-0 ${vis(2)}`}>
              <Modulador familia={f.familia} receta={f.receta} onChange={(nr) => setF((prev) => ({ ...prev, receta: nr }))} />
            </div>
          )}
          {['pabm', 'vtf'].includes(f.familia) && (
            <div className={esMovil && paso === 1 ? 'hidden' : ''}>
            <RecetaConfig familia={f.familia} receta={f.receta} despiece={f.despiece} seccion={seccionReceta}
              onChange={(nr) => setF((prev) => ({ ...prev, receta: nr }))}
              onCostoLinea={(desc, costo) => setF((prev) => ({ ...prev,
                despiece: prev.despiece.map((l) => l.descripcion === desc ? { ...l, costo_unitario: costo } : l) }))}
              onAplicar={(nr, calc, lineas) => setF((prev) => ({
                ...prev, receta: nr,
                ancho_m: calc.vidrio_ancho_mm > 0 ? String((calc.vidrio_ancho_mm / 1000).toFixed(3)) : prev.ancho_m,
                alto_m: calc.vidrio_alto_mm > 0 ? String((calc.vidrio_alto_mm / 1000).toFixed(3)) : prev.alto_m,
                despiece: [...prev.despiece.filter((l) => l.origen !== 'regla' && (l.descripcion || l.item_codigo)), ...lineas],
              }))} />
            </div>
          )}
          <div className={`grid grid-cols-3 gap-2 ${vis(1)}`}>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Ancho (m)</label>
              <Input type="number" step="0.01" value={f.diseno ? (dimsDiseno(f.diseno).ancho_mm / 1000) : f.ancho_m} disabled={!!f.diseno}
                onChange={(e) => setF({ ...f, ancho_m: e.target.value })} className="h-9" data-testid="el-ancho" />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Alto (m)</label>
              <Input type="number" step="0.01" value={f.diseno ? (dimsDiseno(f.diseno).alto_mm / 1000) : f.alto_m} disabled={!!f.diseno}
                onChange={(e) => setF({ ...f, alto_m: e.target.value })} className="h-9" data-testid="el-alto" />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Cantidad</label>
              <Input type="number" min="1" value={f.cantidad} onChange={(e) => setF({ ...f, cantidad: e.target.value })} className="h-9" data-testid="el-cantidad" />
            </div>
          </div>
          {FAMILIAS_DISENO.includes(f.familia) && plantillas.filter((t) => t.familia === f.familia).length > 0 && (
            <div className={`flex items-center gap-2 ${vis(2)}`}>
              <BookmarkSimple size={15} weight="duotone" className="text-[#0EA5E9] shrink-0" />
              <select defaultValue="" onChange={(e) => { if (e.target.value) { aplicarPlantilla(e.target.value); e.target.value = ''; } }}
                className="h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1 flex-1"
                data-testid="el-plantilla-select">
                <option value="">Usar plantilla…</option>
                {plantillas.filter((t) => t.familia === f.familia).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.diseno?.tipo === 'composicion'
                      ? `🧱 ${t.nombre} (Lego: ${(t.diseno?.piezas || []).length} elem. + ${(t.diseno?.herrajes || []).length} herrajes)`
                      : `${t.nombre} (${t.diseno?.ancho_mm}×${t.diseno?.alto_mm} mm)`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {FAMILIAS_DISENO.includes(f.familia) && (
            f.diseno?.tipo === 'composicion' ? (
              <div className={vis(2)}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Compositor Lego — Catálogo Soluarq</p>
                  <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-[#0EA5E9]" onClick={guardarPlantilla} data-testid="el-guardar-plantilla-lego">
                      <BookmarkSimple size={13} className="mr-0.5" /> Guardar como plantilla
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => setF({ ...f, diseno: null })} data-testid="el-quitar-lego">Quitar diseño</Button>
                  </div>
                </div>
                <ComposerLego value={f.diseno} onChange={(nd) => setF({ ...f, diseno: nd })}
                  onAplicar={(lineas) => setF((prev) => ({ ...prev, despiece: lineas.length ? lineas : prev.despiece }))} />
              </div>
            ) : f.diseno ? (
              <div className={vis(2)}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Diseño 2D {f.diseno.version ? `(v${f.diseno.version})` : ''}</p>
                  <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-[#0EA5E9]" onClick={guardarPlantilla} data-testid="el-guardar-plantilla">
                      <BookmarkSimple size={13} className="mr-0.5" /> Guardar como plantilla
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => setF({ ...f, diseno: null })} data-testid="el-quitar-diseno">Quitar diseño</Button>
                  </div>
                </div>
                <Disenador2D value={f.diseno} onChange={(d) => setF({ ...f, diseno: d })} />
              </div>
            ) : (
              <div className={`flex gap-1.5 ${vis(2)}`}>
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs w-fit"
                  onClick={() => setF({ ...f, diseno: { ...disenoDefault(), ancho_mm: Number(f.ancho_m || 1.5) * 1000, alto_mm: Number(f.alto_m || 1.5) * 1000, cols_mm: [Number(f.ancho_m || 1.5) * 1000], rows_mm: [Number(f.alto_m || 1.5) * 1000] } })}
                  data-testid="el-abrir-diseno">
                  <Ruler size={14} className="mr-1" /> Diseñar en 2D
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs w-fit border-emerald-500/60 text-emerald-600"
                  onClick={() => setF({ ...f, diseno: composicionDefault() })} data-testid="el-abrir-lego">
                  🧱 Componer con catálogo (Lego)
                </Button>
              </div>
            )
          )}
          <div className={vis(3)}><VariablesTecnicas familia={f.familia} valores={f.variables} onChange={(v) => setF({ ...f, variables: v })} /></div>
          <div className={vis(5)}>
            {lineasEditor({ keyName: 'despiece', titulo: 'Materiales y cantidades (incluye mano de obra)', conTipo: true })}
            {adminLike && lineasEditor({ keyName: 'otros_costos', titulo: 'Otros costos (fletes, imprevistos…)' })}
          </div>
          <div className={vis(1)}>
            <label className="text-xs font-bold text-[var(--text-muted)]">Notas</label>
            <Input value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} className="h-9" data-testid="el-notas" />
          </div>
          {esMovil && (
            <Button type="button" variant="outline" disabled={saving} onClick={guardar} className="w-full h-10 text-xs" data-testid="wiz-guardar-borrador">
              Guardar borrador y continuar después
            </Button>
          )}
          <Button disabled={saving} onClick={guardar} className={`w-full h-11 bg-[#0EA5E9] hover:bg-[#0284C7] text-white ${esMovil && paso !== 5 ? 'hidden' : ''}`} data-testid="el-guardar">
            {saving ? 'Guardando…' : elemento ? 'Guardar cambios' : 'Agregar elemento'}
          </Button>
          {esMovil && paso === 5 && (
            <p className="text-[10px] text-[var(--text-muted)]">Al guardar, continúa en la pantalla del proyecto con el <b>Paso 6 — Ejecución y mano de obra</b> y el <b>Paso 7 — Resumen y cotización (PDF)</b>.</p>
          )}
        </div>
        </div>
        {/* Resumen lateral (solo desktop grande) — información útil sin robar área de trabajo */}
        <aside className="hidden lg:block sticky top-2 border border-[var(--border)] rounded-lg p-3 space-y-2 bg-[var(--bg)]" data-testid="el-resumen-lateral">
          <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">Resumen</p>
          <p className="text-sm font-black text-[#0EA5E9]">{elemento?.codigo || 'NUEVO'}</p>
          <p className="text-xs font-bold text-[var(--text)]">{f.nombre || FAMILIAS.find((x) => x.v === f.familia)?.label || '—'}</p>
          <p className="text-xs text-[var(--text-muted)]">{(f.diseno ? (dimsDiseno(f.diseno).ancho_mm / 1000).toFixed(3) : f.ancho_m) || '—'} × {(f.diseno ? (dimsDiseno(f.diseno).alto_mm / 1000).toFixed(3) : f.alto_m) || '—'} m · Cant. {f.cantidad || 1}</p>
          {f.receta?.vidrio?.tipo && <p className="text-xs text-[var(--text-muted)]">Vidrio {f.receta.vidrio.tipo} {f.receta.vidrio.espesor || ''}</p>}
          {f.receta?.acabado && <p className="text-xs text-[var(--text-muted)]">Acabado {f.receta.acabado}</p>}
          <p className="text-xs text-[var(--text-muted)]">
            {f.diseno?.tipo === 'composicion'
              ? `${(f.diseno.piezas || []).length} elemento(s) · ${(f.diseno.herrajes || []).length} herraje(s) en el dibujo`
              : `${(f.despiece || []).length} material(es) · ${(f.receta?.herrajes_visuales || []).length} herraje(s) en el dibujo`}
          </p>
          {f.receta?.despiece_estado === 'pendiente'
            ? <p className="text-[11px] font-black text-amber-500" data-testid="el-resumen-estado">⚠️ RECÁLCULO PENDIENTE</p>
            : <p className="text-[11px] font-black text-emerald-500" data-testid="el-resumen-estado">✓ Actualizado</p>}
        </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// APROBAR (+ opcional generar OT)
// =========================================================================
const AprobarDialog = ({ proyecto, onClose, onDone }) => {
  const yaAprobado = proyecto.estado === 'aprobado';
  const [modo, setModo] = useState(yaAprobado ? 'ot' : 'ot');
  const [conAnticipo, setConAnticipo] = useState(true);
  const [ot, setOt] = useState({ anticipo_monto: '', anticipo_documento_url: '', anticipo_documento_nombre: '', bypass_pin: '', bypass_motivo: '', fecha_inicio_estimada: '', fecha_entrega_estimada: '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const subirDoc = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/produccion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOt((o) => ({ ...o, anticipo_documento_url: r.data.url, anticipo_documento_nombre: file.name }));
      toast.success('Documento subido');
    } catch (e) { toast.error(errMsg(e, 'No se pudo subir el documento')); }
    finally { setUploading(false); }
  };

  const confirmar = async () => {
    setSaving(true);
    try {
      if (modo === 'solo') {
        await api.post(`/sa/proyectos/${proyecto.id}/estado`, { estado: 'aprobado' });
        toast.success('Presupuesto aprobado');
      } else {
        const body = {
          fecha_inicio_estimada: ot.fecha_inicio_estimada || undefined,
          fecha_entrega_estimada: ot.fecha_entrega_estimada || undefined,
        };
        if (conAnticipo) {
          body.anticipo_monto = Number(ot.anticipo_monto || 0);
          body.anticipo_documento_url = ot.anticipo_documento_url || undefined;
          body.anticipo_documento_nombre = ot.anticipo_documento_nombre || undefined;
        } else {
          body.bypass_pin = ot.bypass_pin;
          body.bypass_motivo = ot.bypass_motivo;
        }
        const { data } = await api.post(`/sa/proyectos/${proyecto.id}/generar-ot`, body);
        toast.success(`Aprobado — OT ${data.ot_numero} generada`);
      }
      onDone();
    } catch (e) { toast.error(errMsg(e, 'No se pudo completar')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{yaAprobado ? 'Generar Orden de Trabajo' : 'Aprobar presupuesto'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[var(--text)]">
            {yaAprobado ? `Generar la OT para ${proyecto.numero} — ${Q(proyecto.totales?.total)}.`
              : `¿Desea aprobar este presupuesto (${Q(proyecto.totales?.total)}) y generar la Orden de Trabajo?`}
          </p>
          {!yaAprobado && (
            <div className="flex gap-2">
              <button type="button" onClick={() => setModo('solo')} data-testid="aprobar-modo-solo"
                className={`flex-1 border rounded-lg px-3 py-2 text-sm font-semibold ${modo === 'solo' ? 'border-[#0EA5E9] text-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
                Solo aprobar
              </button>
              <button type="button" onClick={() => setModo('ot')} data-testid="aprobar-modo-ot"
                className={`flex-1 border rounded-lg px-3 py-2 text-sm font-semibold ${modo === 'ot' ? 'border-emerald-500 text-emerald-500 bg-emerald-500/10' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
                Aprobar y generar OT
              </button>
            </div>
          )}
          {modo === 'ot' && (
            <div className="space-y-2.5 border border-[var(--border)] rounded-lg p-3">
              <div className="flex gap-2 text-xs font-semibold">
                <button type="button" onClick={() => setConAnticipo(true)} data-testid="ot-con-anticipo"
                  className={`px-2.5 py-1 rounded-full ${conAnticipo ? 'bg-[#0EA5E9]/15 text-[#0EA5E9]' : 'text-[var(--text-muted)]'}`}>Con anticipo</button>
                <button type="button" onClick={() => setConAnticipo(false)} data-testid="ot-sin-anticipo"
                  className={`px-2.5 py-1 rounded-full ${!conAnticipo ? 'bg-amber-500/15 text-amber-500' : 'text-[var(--text-muted)]'}`}>Sin anticipo (PIN)</button>
              </div>
              {conAnticipo ? (
                <>
                  <div>
                    <label className="text-xs font-bold text-[var(--text-muted)]">Monto del anticipo (Q)</label>
                    <Input type="number" value={ot.anticipo_monto} onChange={(e) => setOt({ ...ot, anticipo_monto: e.target.value })} className="h-9" data-testid="ot-anticipo-monto" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[var(--text-muted)]">Documento de respaldo (depósito / recibo)</label>
                    <input type="file" accept="image/*,application/pdf" disabled={uploading}
                      onChange={(e) => e.target.files?.[0] && subirDoc(e.target.files[0])}
                      className="block w-full text-xs mt-1" data-testid="ot-anticipo-doc" />
                    {uploading && <p className="text-[11px] text-[#0EA5E9]">Subiendo…</p>}
                    {ot.anticipo_documento_url && <p className="text-[11px] text-emerald-500">✓ {ot.anticipo_documento_nombre}</p>}
                  </div>
                </>
              ) : (
                <>
                  <Input type="password" placeholder="PIN de autorización" value={ot.bypass_pin} onChange={(e) => setOt({ ...ot, bypass_pin: e.target.value })} className="h-9" data-testid="ot-pin" />
                  <Input placeholder="Motivo (mínimo 5 caracteres)" value={ot.bypass_motivo} onChange={(e) => setOt({ ...ot, bypass_motivo: e.target.value })} className="h-9" data-testid="ot-motivo" />
                </>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)]">Inicio estimado</label>
                  <Input type="date" value={ot.fecha_inicio_estimada} onChange={(e) => setOt({ ...ot, fecha_inicio_estimada: e.target.value })} className="h-9" data-testid="ot-fecha-inicio" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)]">Entrega estimada</label>
                  <Input type="date" value={ot.fecha_entrega_estimada} onChange={(e) => setOt({ ...ot, fecha_entrega_estimada: e.target.value })} className="h-9" data-testid="ot-fecha-entrega" />
                </div>
              </div>
            </div>
          )}
          <Button disabled={saving || uploading} onClick={confirmar}
            className={`w-full text-white ${modo === 'ot' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#0EA5E9] hover:bg-[#0284C7]'}`}
            data-testid="aprobar-confirmar">
            {saving ? 'Procesando…' : modo === 'ot' ? (yaAprobado ? 'Generar OT' : 'Aprobar y generar OT') : 'Aprobar presupuesto'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function PresupuestosSA() {
  const { id } = useParams();
  return id ? <Editor id={id} /> : <Lista />;
}
